// sync.js — account-free, end-to-end encrypted sync + backups + lock-screen rest alerts.
//
// Privacy model
//   A 256-bit random "sync key" lives only on the user's devices (shown as a
//   54-character code and as a link https://<origin>/#sync=<code>; the #fragment
//   never reaches any server). From it we derive, with WebCrypto:
//     vaultId = hex SHA-256("liftlog/vault/v1" ‖ key)    → the server-side address
//     aesKey  = HKDF-SHA256(key, "liftlog/sync/v1")      → AES-256-GCM
//   The whole app document is gzip'd + encrypted client side; the server stores
//   one opaque blob per vault and never sees the key or plaintext.
//
// Data model (the "sync doc", format v2)
//   { v: 2, c: { <collection>: { <id>: { t, h, d, ft? } } },  live records
//            x: { <collection>: { <id>: deletedAt } },         tombstones
//            p: { <prefKey>: { t, v } },                       prefs, per key
//            s: { <scalar>: { t, v } } }                       activeProgramId, profile, welcomed, userName
//   t = when the record was last edited (ms), h = content hash, d = the record.
//   Programs also carry ft = { <field path>: t }: every program field (name,
//   each training max, each day, each exercise's target / sets / scheme state,
//   the day and exercise order) has its own timestamp and merges on its own, so
//   a progression bump on one phone and a rename on another both survive.
//
// Edit times are real: an edit tracker (observeLocalState, driven by the React
// bridge on every state change, whether sync is on or not) keeps a sidecar
// index (localStorage 'liftlog.syncMeta': hash + time per record / field / pref)
// and stamps a record the moment its content changes. A brand-new device's
// untouched defaults have t = 0 ("never edited"), so they never beat a real edit.
//
// Deletes are explicit: only a user delete (store actions write
// state.deleted[collection][id] = deletedAt) creates a tombstone. A record that
// merely vanishes (restoring an older backup, a reset, a bulk replace) is NOT a
// delete — the other copy brings it back. Restored records are stamped with the
// backup's own date, so newer edits elsewhere win: a restore merges, it never
// deletes or rolls back data on other devices.
//
// Merge (pure, commutative, idempotent): per record / field / pref the newest
// edit wins; equal times fall back to the content hash (only ever between two
// real edits made in the same millisecond, or two untouched defaults). A
// tombstone wins only over versions that are not newer than the delete.
//
// Joining a vault: the vault's settings, name and active program win over the
// joining device's (or the other way round when the user picks "this device"),
// and a duplicate program the joining device made during onboarding is folded
// into the vault's copy instead of being added twice.
//
// Performance: hashes and JSON are cached per record object (WeakMap), the wire
// format is one line per record so decoding re-uses unchanged records without
// parsing them, and every long loop yields to the main thread every ~8 ms.
// Logging sets in a live workout does not trigger a sync (live sessions are not
// synced); while a workout is live, other changes wait for the app being hidden,
// the 5-minute poll, or the workout finishing.
//
// Public API (see bottom): initSync, notifyLocalChange, observeLocalState,
// syncNow, enableSync, joinSync, previewJoin, disableSync, replaceSyncedCopy,
// probeSyncServer, getSyncStatus, subscribeSyncStatus, formatSyncCode,
// syncLinkFor, readSyncLinkFromLocation, backupToFile, backupNudgeDue,
// dismissBackupNudge, pushRestAlert, cancelRestAlert, enableRestPush,
// disableRestPush, restPushState, configureSyncDefaults, resetSyncTracking,
// plus pure helpers for tests.

import React from 'react';
import { nextWorkout } from './progression.js';
import { historyOf } from './history.js';
import { computePRs as prsOf, warmPRs } from './prs.js';

// ───────────────────────────── environment ─────────────────────────────

const K = {
  state: 'liftlog.v1',
  cfg: 'liftlog.sync',
  meta: 'liftlog.syncMeta',
  base: 'liftlog.syncBase',
  push: 'liftlog.pushAlerts',
  backup: 'liftlog.backup',
  account: 'liftlog.account',
};
// photos = progress-photo records ({ id, createdAt, date, pose }); the images
// themselves sync as separate encrypted blobs (photo_sync.js).
const COLLECTIONS = ['sessions', 'exercises', 'programs', 'bodyEntries', 'measurements', 'photos'];
const SCALARS = ['activeProgramId', 'profile', 'welcomed', 'userName'];
const SCALAR_DEFAULTS = { activeProgramId: null, profile: null, welcomed: false, userName: '' };
const TOMBSTONE_TTL = 400 * 86400000;
const DEBOUNCE_MS = 2500;
const POLL_MS = 5 * 60000;
const FOCUS_MIN_MS = 15000;
const DOC_V = 2;

const env = {
  storage: null,
  fetch: null,
  base: null,
};

/** Override environment pieces (tests / dev). Flushes and resets per-device state. */
export function setSyncEnvironment(o = {}) {
  flushMeta();
  Object.assign(env, o);
  keyCache = null; status = null; lastPullAt = 0; T.meta = null; pendingLive = false; lastDoc = null;
  accountCheckedThisLaunch = false;
}

function store() {
  if (env.storage) return env.storage;
  try { if (typeof localStorage !== 'undefined') return localStorage; } catch (_) { /* blocked */ }
  return memStorage;
}
const memStorage = (() => { const m = new Map(); return { getItem: (k) => (m.has(k) ? m.get(k) : null), setItem: (k, v) => m.set(k, String(v)), removeItem: (k) => m.delete(k) }; })();
function lsGet(k, fallback = null) { try { const v = store().getItem(k); return v == null ? fallback : JSON.parse(v); } catch (_) { return fallback; } }
function lsSet(k, v) { try { store().setItem(k, JSON.stringify(v)); return true; } catch (_) { return false; } }
function lsDel(k) { try { store().removeItem(k); } catch (_) { /* ignore */ } }
const doFetch = (...a) => (env.fetch || globalThis.fetch)(...a);

/** API base: localStorage 'liftlog.syncBase' (dev) → VITE_SYNC_BASE → same-origin '/api'. */
export function syncBase() {
  if (env.base) return env.base.replace(/\/$/, '');
  try { const b = store().getItem(K.base); if (b) return b.replace(/\/$/, ''); } catch (_) { /* ignore */ }
  let v = '';
  try { v = import.meta.env && import.meta.env.VITE_SYNC_BASE; } catch (_) { /* not vite */ }
  return (v || '/api').replace(/\/$/, '');
}

const subtle = () => globalThis.crypto && globalThis.crypto.subtle;
const te = new TextEncoder();
const td = new TextDecoder();

// Cooperative scheduling: long loops give the main thread back every ~8 ms.
const perfNow = () => (globalThis.performance && performance.now ? performance.now() : Date.now());
function yieldMain() {
  return new Promise((resolve) => {
    if (globalThis.scheduler && typeof globalThis.scheduler.yield === 'function') { globalThis.scheduler.yield().then(resolve, resolve); return; }
    if (typeof MessageChannel !== 'undefined') {
      const ch = new MessageChannel();
      ch.port1.onmessage = () => { ch.port1.close(); resolve(); };
      ch.port2.postMessage(0);
      return;
    }
    setTimeout(resolve, 0);
  });
}
/** Returns () => Promise|null: a yield once the slice budget is used up. */
function slicer(ms = 8) {
  let t0 = perfNow();
  return () => (perfNow() - t0 < ms ? null : yieldMain().then(() => { t0 = perfNow(); }));
}

// ───────────────────────────── key code ─────────────────────────────

const B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32

function b32encode(bytes) {
  let bits = 0, val = 0, out = '';
  for (const b of bytes) {
    val = (val << 8) | b; bits += 8;
    while (bits >= 5) { out += B32[(val >>> (bits - 5)) & 31]; bits -= 5; }
  }
  if (bits > 0) out += B32[(val << (5 - bits)) & 31];
  return out;
}
function b32decode(str, nBytes) {
  let bits = 0, val = 0;
  const out = [];
  for (const ch of str) {
    const i = B32.indexOf(ch);
    if (i < 0) return null;
    val = ((val << 5) | i) & 0xffff; bits += 5;
    if (bits >= 8) { out.push((val >>> (bits - 8)) & 255); bits -= 8; }
  }
  return out.length >= nBytes ? new Uint8Array(out.slice(0, nBytes)) : null;
}
function checksum(bytes) {
  let a = 1, b = 0;
  for (const x of bytes) { a = (a + x) % 1021; b = (b + a) % 1021; }
  const v = (a * 31 + b) % 1024;
  return B32[v >> 5] + B32[v & 31];
}

/** 32 random bytes → 54-char code (52 key chars + 2 checksum chars). */
export function newSyncCode() {
  const key = new Uint8Array(32);
  globalThis.crypto.getRandomValues(key);
  return b32encode(key) + checksum(key);
}
/** Normalize user input (spaces, dashes, lowercase, I/L→1, O→0). Returns compact code or null. */
export function normalizeSyncCode(input) {
  if (!input) return null;
  let s = String(input).trim();
  const m = /#sync=([A-Za-z0-9-]+)/.exec(s);
  if (m) s = m[1];
  s = s.toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/[IL]/g, '1').replace(/O/g, '0');
  if (s.length !== 54) return null;
  const key = b32decode(s.slice(0, 52), 32);
  if (!key || checksum(key) !== s.slice(52)) return null;
  return s;
}
/** "ABCDEF-GHJKMN-…" groups of 6 for display. */
export function formatSyncCode(code) { return (code || '').match(/.{1,6}/g)?.join('-') || ''; }
export function syncLinkFor(code, origin) {
  const o = origin || (typeof location !== 'undefined' ? location.origin + location.pathname.replace(/[^/]*$/, '') : '');
  return `${o.replace(/\/$/, '')}/#sync=${code}`;
}
/** If the page was opened via a sync link, return the code and strip it from the URL. */
export function readSyncLinkFromLocation() {
  try {
    const m = /[#&]sync=([A-Za-z0-9-]+)/.exec(location.hash || '');
    if (!m) return null;
    const code = normalizeSyncCode(m[1]);
    history.replaceState(null, '', location.pathname + location.search);
    return code;
  } catch (_) { return null; }
}

// ───────────────────────────── crypto + wire format ─────────────────────────────

const hex = (buf) => Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
let keyCache = null;

export async function deriveKeys(code) {
  if (keyCache && keyCache.code === code) return keyCache;
  const raw = b32decode(code.slice(0, 52), 32);
  const vaultId = hex(await subtle().digest('SHA-256', concat(te.encode('liftlog/vault/v1'), raw)));
  const ikm = await subtle().importKey('raw', raw, 'HKDF', false, ['deriveKey']);
  const aesKey = await subtle().deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: te.encode('liftlog/sync/v1'), info: te.encode('aes-256-gcm') },
    ikm, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
  );
  keyCache = { code, vaultId, aesKey };
  return keyCache;
}
function concat(...arrs) {
  const out = new Uint8Array(arrs.reduce((n, a) => n + a.length, 0));
  let o = 0; for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
}
// A byte array as a stream of small chunks (so no single (de)compression step is long).
function chunked(bytes, size = 16384) {
  let i = 0;
  return new ReadableStream({
    pull(c) {
      if (i >= bytes.length) { c.close(); return; }
      c.enqueue(bytes.subarray(i, i + size)); i += size;
    },
  });
}
const MAGIC = [0x4c, 0x4c]; // 'L','L' + format byte (1 = JSON doc, 2 = line format)

// Per-record caches keyed by the record object itself (records are immutable).
const memo = new WeakMap();      // record → content hash
const jsonCache = new WeakMap(); // record → JSON text (wire encoding)
const flatCache = new WeakMap(); // program → flattened fields (or null)

/**
 * doc → encrypted bytes: 'L' 'L' 2 | flags(1: bit0 = gzip) | iv(12) | AES-GCM ciphertext.
 * Plaintext: a JSON header line ({ v, app, at, by, x, p, s }), then one line per
 * record: JSON [collection, id, t, h, ft|0] TAB JSON(record).
 */
export async function encryptDoc(doc, keys) {
  let body, flags = 0;
  if (typeof CompressionStream !== 'undefined') {
    // Stream the text through gzip ~128 KB at a time: the 5 MB string of a big
    // history is never built (or encoded) in one piece.
    const cs = new CompressionStream('gzip');
    const writer = cs.writable.getWriter();
    const done = new Response(cs.readable).arrayBuffer();
    try {
      await serializeDoc(doc, (bytes) => writer.write(bytes));
      await writer.close();
    } catch (e) { writer.abort(e).catch(() => {}); throw e; }
    body = new Uint8Array(await done);
    flags |= 1;
  } else {
    const parts = [];
    await serializeDoc(doc, (bytes) => { parts.push(bytes); });
    body = concat(...parts);
  }
  const iv = new Uint8Array(12);
  globalThis.crypto.getRandomValues(iv);
  const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: te.encode(keys.vaultId) }, keys.aesKey, body));
  return concat(new Uint8Array([...MAGIC, DOC_V]), new Uint8Array([flags]), iv, ct);
}
async function serializeDoc(doc, sink) {
  let buf = [], size = 0;
  const flush = async () => { if (!size) return; const bytes = te.encode(buf.join('')); buf = []; size = 0; await sink(bytes); };
  const head = { v: DOC_V, app: 'liftlog', at: doc.at || 0, by: doc.by || '', x: doc.x || {}, p: doc.p || {}, s: doc.s || {} };
  buf.push(JSON.stringify(head)); size = buf[0].length;
  const slice = slicer();
  for (const coll of COLLECTIONS) {
    const map = (doc.c && doc.c[coll]) || {};
    for (const id of Object.keys(map)) {
      const e = map[id];
      let body = e.d && typeof e.d === 'object' ? jsonCache.get(e.d) : undefined;
      if (body === undefined) { body = JSON.stringify(e.d ?? null); if (e.d && typeof e.d === 'object') jsonCache.set(e.d, body); }
      const line = '\n' + JSON.stringify([coll, id, e.t, e.h ?? hashEntity(e.d), e.ft || 0]) + '\t' + body;
      buf.push(line); size += line.length;
      if (size >= 131072) await flush();
      const y = slice(); if (y) await y;
    }
  }
  await flush();
}

/**
 * Encrypted bytes → doc. opts.reuse(coll, id, h) may return an existing record
 * object with the same content hash (skips parsing it). Errors carry .code:
 * 'format' (not Lift Log data / damaged), 'decrypt' (authentication failed),
 * 'newer' (written by a newer app version).
 */
export async function decryptDoc(bytes, keys, opts = {}) {
  const b = new Uint8Array(bytes);
  // Format bytes 1..DOC_V are read here; a small number above that is a newer app's format.
  if (b.length < 17 || b[0] !== MAGIC[0] || b[1] !== MAGIC[1] || b[2] < 1 || b[2] > 15 || b[3] > 1) throw syncError('format', 'Unrecognized sync data');
  if (b[2] > DOC_V) throw syncError('newer', 'The synced copy was saved by a newer version of Lift Log. Update this app to keep syncing.');
  const flags = b[3];
  let plain;
  try {
    plain = new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: b.slice(4, 16), additionalData: te.encode(keys.vaultId) }, keys.aesKey, b.slice(16)));
  } catch (_) { throw syncError('decrypt', 'The synced copy can’t be unlocked with this code'); }
  try {
    let stream = chunked(plain, flags & 1 ? 8192 : 131072);
    if (flags & 1) stream = stream.pipeThrough(new DecompressionStream('gzip'));
    if (b[2] === 1) return upgradeV1Doc(JSON.parse(await new Response(stream).text()));
    return await parseV2(stream, opts.reuse);
  } catch (e) {
    if (e && e.code) throw e;
    throw syncError('format', 'The synced copy is damaged');
  }
}
// Parse the line format as the bytes arrive: one small JSON.parse per record.
async function parseV2(byteStream, reuse) {
  const reader = byteStream.getReader();
  const dec = new TextDecoder();
  let doc = null;
  const line = (text) => {
    if (!doc) {
      const head = JSON.parse(text);
      if (!head || head.app !== 'liftlog' || typeof head.v !== 'number') throw syncError('format', 'Unrecognized sync data');
      if (head.v > DOC_V) throw syncError('newer', 'The synced copy was saved by a newer version of Lift Log. Update this app to keep syncing.');
      doc = { v: DOC_V, app: 'liftlog', at: head.at || 0, by: head.by || '', c: {}, x: head.x || {}, p: head.p || {}, s: head.s || {} };
      for (const coll of COLLECTIONS) doc.c[coll] = {};
      return;
    }
    const tab = text.indexOf('\t');
    if (tab <= 0) return;
    const [coll, id, t, h, ft] = JSON.parse(text.slice(0, tab));
    if (!doc.c[coll]) return;
    let d = reuse ? reuse(coll, id, h) : undefined;
    if (d === undefined) {
      const body = text.slice(tab + 1);
      d = JSON.parse(body);
      if (d && typeof d === 'object') { memo.set(d, h); jsonCache.set(d, body); }
    }
    doc.c[coll][id] = ft ? { t, h, d, ft } : { t, h, d };
  };
  const slice = slicer();
  let carry = '';
  for (;;) {
    const { value, done } = await reader.read();
    carry += done ? dec.decode() : dec.decode(value, { stream: true });
    let start = 0, nl;
    while ((nl = carry.indexOf('\n', start)) >= 0) {
      line(carry.slice(start, nl));
      start = nl + 1;
      const y = slice(); if (y) await y;
    }
    carry = carry.slice(start);
    if (done) break;
  }
  if (carry) line(carry);
  if (!doc) throw syncError('format', 'Unrecognized sync data');
  return doc;
}
/** A format-1 doc ({ c: { coll: { id: { t, d } } }, … }) → format 2. */
function upgradeV1Doc(old) {
  if (!old || old.app !== 'liftlog' || old.v !== 1) throw syncError('format', 'Unrecognized sync data');
  const doc = { v: DOC_V, app: 'liftlog', at: old.at || 0, by: old.by || '', c: {}, x: old.x || {}, p: old.p || {}, s: old.s || {} };
  for (const coll of COLLECTIONS) {
    doc.c[coll] = {};
    for (const [id, e] of Object.entries((old.c && old.c[coll]) || {})) {
      if (!e || !e.d) continue;
      const rec = { t: e.t || 0, h: hashEntity(e.d), d: e.d };
      if (coll === 'programs') { const f = flatOf(e.d); if (f) { rec.ft = {}; for (const p of Object.keys(f.fh)) rec.ft[p] = rec.t; } }
      doc.c[coll][id] = rec;
    }
  }
  return doc;
}
function syncError(code, message) { const e = new Error(message); e.code = code; return e; }

// ───────────────────────────── hashing ─────────────────────────────

function stable(v) {
  if (v === null || typeof v !== 'object') return JSON.stringify(v === undefined ? null : v);
  if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
  const keys = Object.keys(v).filter((k) => v[k] !== undefined).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + stable(v[k])).join(',') + '}';
}
// cyrb53 — fast, well-distributed 53-bit string hash (not cryptographic; change detection only).
function cyrb53(str) {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761); h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
export const hashOf = (v) => cyrb53(stable(v));
function hashEntity(e) {
  if (!e || typeof e !== 'object') return hashOf(e);
  let h = memo.get(e);
  if (h === undefined) { h = hashOf(e); memo.set(e, h); }
  return h;
}
const recHash = (e) => (e.h != null ? e.h : (e.d !== undefined ? hashEntity(e.d) : hashOf(e.v)));
/** Hash of a doc's content (ignores envelope fields and program field times). */
export function docHash(doc) {
  const parts = [];
  for (const coll of COLLECTIONS) {
    const map = (doc.c && doc.c[coll]) || {};
    for (const id of Object.keys(map).sort()) { const e = map[id]; parts.push(coll, id, recHash(e), e.t); }
  }
  parts.push(stable(doc.x || {}), stable(doc.p || {}), stable(doc.s || {}));
  return cyrb53(parts.join('|'));
}

// ───────────────────────────── programs, field by field ─────────────────────────────
//
// A program flattens into field paths (SEP-separated):
//   f␟<key>          top-level field (name, weeks, periodization, cycle, …)
//   T / t␟<key>      "has a tms object" marker / one training max
//   D                order of day ids
//   d␟<dayId>␟<key>  one day field          x␟<dayId>  order of exercise ids in the day
//   e␟<peId>␟<key>   one program-exercise field (targetWeight, sets, schemeState, …)
// Programs whose day / exercise ids are missing or repeated fall back to
// whole-record merging.

const SEP = '\u001f';
export function flattenProgram(p) {
  if (!p || typeof p !== 'object' || !Array.isArray(p.days)) return null;
  const out = {};
  for (const k of Object.keys(p)) {
    const v = p[k];
    if (k === 'days' || v === undefined) continue;
    if (k === 'tms' && v && typeof v === 'object' && !Array.isArray(v)) continue;
    out['f' + SEP + k] = v;
  }
  if (p.tms && typeof p.tms === 'object' && !Array.isArray(p.tms)) {
    out.T = true;
    for (const k of Object.keys(p.tms)) if (p.tms[k] !== undefined) out['t' + SEP + k] = p.tms[k];
  }
  const order = [];
  const dayIds = new Set(), peIds = new Set();
  for (const d of p.days) {
    if (!d || typeof d !== 'object' || d.id == null || !Array.isArray(d.exercises)) return null;
    const did = String(d.id);
    if (dayIds.has(did) || did.includes(SEP)) return null;
    dayIds.add(did); order.push(did);
    for (const k of Object.keys(d)) { if (k === 'exercises' || d[k] === undefined) continue; out['d' + SEP + did + SEP + k] = d[k]; }
    const ex = [];
    for (const pe of d.exercises) {
      if (!pe || typeof pe !== 'object' || pe.id == null) return null;
      const pid = String(pe.id);
      if (peIds.has(pid) || pid.includes(SEP)) return null;
      peIds.add(pid); ex.push(pid);
      for (const k of Object.keys(pe)) { if (pe[k] === undefined) continue; out['e' + SEP + pid + SEP + k] = pe[k]; }
    }
    out['x' + SEP + did] = ex;
  }
  out.D = order;
  return out;
}
export function assembleProgram(flat) {
  const p = {};
  const days = {}, pes = {};
  let tms;
  if (flat.T) tms = {};
  for (const path of Object.keys(flat)) {
    const v = flat[path];
    const i = path.indexOf(SEP);
    const kind = i < 0 ? path : path.slice(0, i);
    if (kind === 'f') p[path.slice(2)] = v;
    else if (kind === 't') { if (tms) tms[path.slice(2)] = v; }
    else if (kind === 'd' || kind === 'e') {
      const j = path.indexOf(SEP, 2);
      const id = path.slice(2, j), key = path.slice(j + 1);
      ((kind === 'd' ? days : pes)[id] ||= {})[key] = v;
    }
  }
  if (tms) p.tms = tms;
  const placed = new Set();
  p.days = (flat.D || []).filter((id) => days[id]).map((id) => ({
    ...days[id],
    exercises: (flat['x' + SEP + id] || []).filter((pid) => pes[pid] && !placed.has(pid) && placed.add(pid)).map((pid) => ({ ...pes[pid] })),
  }));
  return p;
}
function flatOf(p) {
  if (flatCache.has(p)) return flatCache.get(p);
  const flat = flattenProgram(p);
  let r = null;
  if (flat) {
    const fh = {};
    for (const k of Object.keys(flat)) fh[k] = hashOf(flat[k]);
    const pes = new Set();
    for (const did of flat.D) for (const pid of flat['x' + SEP + did] || []) pes.add(pid);
    r = { flat, fh, days: new Set(flat.D), pes };
  }
  if (p && typeof p === 'object') flatCache.set(p, r);
  return r;
}
// Which element (day / program exercise) a path belongs to, if any.
function elementOf(path) {
  const k = path[0];
  if (path[1] !== SEP) return null;
  if (k === 'x') return ['day', path.slice(2)];
  if (k === 'd' || k === 'e') { const j = path.indexOf(SEP, 2); return [k === 'd' ? 'day' : 'pe', path.slice(2, j)]; }
  return null;
}
function elementGone(path, f) {
  const el = elementOf(path);
  if (!el) return false;
  return el[0] === 'day' ? !f.days.has(el[1]) : !f.pes.has(el[1]);
}

// Merge an order list (day order or exercise order): the newer list wins, plus
// any element the winner never knew about (edited after the winner was written).
function mergeOrder(la, ta, lb, tb, elemT) {
  if (!la && !lb) return null;
  let W, L, tw;
  if (!lb || (la && (ta > tb || (ta === tb && hashOf(la) >= hashOf(lb))))) { W = la; L = lb; tw = ta; } else { W = lb; L = la; tw = tb; }
  const out = W.slice();
  let added = false;
  if (L) {
    for (let i = 0; i < L.length; i++) {
      const id = L[i];
      if (out.includes(id) || !(elemT(id) > tw)) continue;
      let at = 0;
      for (let k = i - 1; k >= 0; k--) { const j = out.indexOf(L[k]); if (j >= 0) { at = j + 1; break; } }
      out.splice(at, 0, id);
      added = true;
    }
  }
  return { list: out, t: added ? Math.max(ta ?? -Infinity, tb ?? -Infinity) + 1 : Math.max(ta ?? -Infinity, tb ?? -Infinity) };
}

const ABSENT = '\u0000';
/** Field-wise merge of two versions of one program. Pure, commutative. */
function mergeProgram(a, b) {
  const fa = flatOf(a.d), fb = flatOf(b.d);
  if (!fa || !fb || !a.ft || !b.ft) return newer(a, b);
  if (recHash(a) === recHash(b)) {
    const ft = { ...a.ft };
    for (const k of Object.keys(b.ft)) ft[k] = Math.max(ft[k] ?? -Infinity, b.ft[k]);
    return { t: Math.max(a.t, b.t), h: recHash(a), d: (a.t >= b.t ? a : b).d, ft };
  }
  const flat = {}, ft = {};
  const paths = new Set([...Object.keys(fa.flat), ...Object.keys(fb.flat), ...Object.keys(a.ft), ...Object.keys(b.ft)]);
  const orders = [];
  for (const path of paths) {
    if (path === 'D' || (path[0] === 'x' && path[1] === SEP)) { orders.push(path); continue; }
    const inA = path in fa.flat, inB = path in fb.flat;
    const ta = a.ft[path] ?? (inA ? 0 : -Infinity);
    const tb = b.ft[path] ?? (inB ? 0 : -Infinity);
    let useA;
    if (ta !== tb) useA = ta > tb;
    else useA = (inA ? fa.fh[path] : ABSENT) >= (inB ? fb.fh[path] : ABSENT);
    const t = Math.max(ta, tb);
    if (t > -Infinity) ft[path] = t;
    if (useA ? inA : inB) flat[path] = useA ? fa.flat[path] : fb.flat[path];
  }
  // Newest edit per element, for the order merges.
  const elT = { day: {}, pe: {} };
  for (const path of Object.keys(ft)) {
    const el = elementOf(path);
    if (el && path[0] !== 'x') elT[el[0]][el[1]] = Math.max(elT[el[0]][el[1]] ?? -Infinity, ft[path]);
  }
  for (const path of orders) {
    const ta = a.ft[path] ?? (path in fa.flat ? 0 : -Infinity), tb = b.ft[path] ?? (path in fb.flat ? 0 : -Infinity);
    const r = mergeOrder(fa.flat[path], ta, fb.flat[path], tb, path === 'D'
      ? (id) => Math.max(elT.day[id] ?? -Infinity, a.ft['x' + SEP + id] ?? -Infinity, b.ft['x' + SEP + id] ?? -Infinity)
      : (id) => elT.pe[id] ?? -Infinity);
    if (!r) continue;
    flat[path] = r.list; ft[path] = r.t;
    if (path !== 'D') elT.day[path.slice(2)] = Math.max(elT.day[path.slice(2)] ?? -Infinity, r.t);
  }
  const d = assembleProgram(flat);
  // Keep only field times for paths that still exist or are field-level removals.
  const f = flatOf(d);
  const outFt = {};
  for (const path of Object.keys(ft)) if (!f || !elementGone(path, f)) outFt[path] = ft[path];
  let t = -Infinity;
  for (const v of Object.values(outFt)) if (v > t) t = v;
  return { t: Math.max(t, 0), h: hashEntity(d), d, ft: outFt };
}

// ───────────────────────────── edit tracking (index) ─────────────────────────────

let PREF_DEFAULTS = {};
let BUILTIN_HASH = {};
/** Store registers its defaults so untouched values index as "never edited" (t = 0). */
export function configureSyncDefaults({ prefs, exercises } = {}) {
  if (prefs) PREF_DEFAULTS = prefs;
  if (exercises) { BUILTIN_HASH = {}; for (const e of exercises) if (e && e.id != null) BUILTIN_HASH[String(e.id)] = hashEntity(e); }
}

function itemsOf(state, coll) {
  const arr = Array.isArray(state && state[coll]) ? state[coll] : [];
  const out = arr.filter((e) => e && e.id != null);
  if (coll === 'photos') return out.filter((p) => !p.dataUrl); // a legacy inline image not yet moved to IndexedDB stays local
  return coll === 'sessions' ? out.filter((s) => !!s.completedAt) : out;
}
function scalarsOf(state) {
  return {
    activeProgramId: state.activeProgramId ?? null,
    profile: state.profile ?? null,
    welcomed: !!state.welcomed,
    userName: (state.user && state.user.name) || '',
  };
}
const isUnset = (v) => v == null || v === '' || v === false;
// Time for a record seen for the first time on a device whose edits were never tracked.
function legacyTime(coll, e, h) {
  if (coll === 'sessions') return e.completedAt || e.startedAt || 1;
  if ((coll === 'bodyEntries' || coll === 'measurements') && e.date) { const t = Date.parse(e.date); return Number.isFinite(t) ? t : 1; }
  if (coll === 'exercises' && BUILTIN_HASH[String(e.id)] === h) return 0; // untouched built-in
  if (coll === 'photos') { const t = Date.parse(e.createdAt || e.date || ''); return Number.isFinite(t) ? t : 1; }
  return 1;
}
export function emptyMeta() { return { v: 2, init: false, c: {}, pf: {}, p: {}, s: {}, x: {} }; }
function upgradeMeta(m) {
  if (!m || typeof m !== 'object') return emptyMeta();
  if (m.v === 2) return m;
  // Format-1 meta (old sync engine): same hashes; no field times; its tombstones are kept.
  return { v: 2, init: !!m.init, c: m.c || {}, pf: {}, p: m.p || {}, s: m.s || {}, x: m.x || {} };
}

/**
 * Index local state against the sidecar meta → { doc, meta, changes }. Pure
 * (apart from per-object caches). Changed content → t = now (or the restored
 * backup's date for content a restore brought in); records that vanish are NOT
 * tombstoned — only state.deleted (explicit user deletes) creates tombstones.
 */
export function indexState(state, meta = emptyMeta(), now = Date.now(), deviceId = '') {
  const m = upgradeMeta(meta);
  const st = state || {};
  const first = !m.init;
  const rs = st.syncRestore && st.syncRestore.id != null && st.syncRestore.id !== m.restoreId ? st.syncRestore : null;
  const restoreAt = rs ? Math.max(0, Number(rs.asOf) || 0) : null;
  // Backups carry each record's last edit time (recordTimes): a restored record
  // is dated by that, capped at the backup's date — a backup taken on a device
  // whose copy was stale never outranks edits made elsewhere before it.
  const rtimes = rs && rs.times && typeof rs.times === 'object' ? rs.times : null;
  const restoredAt = (part, coll, id) => {
    const v = rtimes && (coll ? rtimes[part] && rtimes[part][coll] && rtimes[part][coll][id] : rtimes[part] && rtimes[part][id]);
    return Number.isFinite(v) && v >= 0 ? Math.min(v, restoreAt) : restoreAt;
  };
  // Hybrid logical clock: an edit is stamped after everything this device has
  // seen (its own stamps and every synced record), so a device whose clock is
  // behind still orders its edits after the versions it already received.
  const hlc0 = Number(m.hlc) || 0;
  const base = Math.max(now, hlc0 + 1);
  let hlc = hlc0;
  const stamp = (prevT) => {
    if (restoreAt != null) return restoreAt;
    const t = prevT != null ? Math.max(base, prevT + 1) : base;
    if (t > hlc) hlc = t;
    return t;
  };
  let changes = 0;
  // A record that vanished without a delete, or whose time went backwards
  // (restore, undo restore, reset), means this device's copy is no longer a
  // superset of the synced copy: the next sync must merge against the vault
  // instead of trusting a 304.
  let regressed = !!rs;
  const doc = { v: DOC_V, app: 'liftlog', c: {}, x: {}, p: {}, s: {}, at: now, by: deviceId };
  const next = { v: 2, init: true, c: {}, pf: {}, p: {}, s: {}, x: {}, restoreId: st.syncRestore ? st.syncRestore.id : m.restoreId };
  const deleted = st.deleted && typeof st.deleted === 'object' ? st.deleted : {};
  for (const coll of COLLECTIONS) {
    const prevIdx = m.c[coll] || {};
    const prevTomb = m.x[coll] || {};
    const userTomb = deleted[coll] || {};
    const live = {}, idx = {};
    for (const e of itemsOf(st, coll)) {
      const id = String(e.id);
      if (live[id]) continue; // duplicate id in local data: first wins
      const h = hashEntity(e);
      const pm = prevIdx[id];
      // Back after a delete this device knew about (e.g. a restore brought it back) = re-created.
      const undelete = !pm && prevTomb[id] != null && userTomb[id] == null;
      if (coll === 'programs') {
        const r = indexProgram(e, h, pm, m.pf[id], first, stamp, undelete ? prevTomb[id] : null, restoreAt != null && undelete ? restoreAt : base);
        if (r.changed) changes++;
        if (pm && r.rec.t < pm[1]) regressed = true;
        if (r.rec.t > hlc) hlc = r.rec.t;
        live[id] = r.rec; idx[id] = [h, r.rec.t];
        if (r.pf) next.pf[id] = r.pf;
        continue;
      }
      let t;
      if (pm && pm[0] === h) t = pm[1];
      // Back after a delete. Through a restore it counts from the backup's date
      // (it beats the delete, not edits made on other devices after the delete).
      else if (undelete) { t = Math.max(restoreAt != null ? restoredAt('c', coll, id) : base, prevTomb[id] + 1); if (t > hlc) hlc = t; changes++; }
      else if (first) t = legacyTime(coll, e, h);
      else if (!pm && coll === 'exercises' && BUILTIN_HASH[id] === h) t = 0;
      else { t = restoreAt != null ? restoredAt('c', coll, id) : stamp(pm ? pm[1] : null); changes++; if (pm && t < pm[1]) regressed = true; }
      live[id] = { t, h, d: e }; idx[id] = [h, t];
    }
    if (!first) for (const id of Object.keys(prevIdx)) if (!live[id] && userTomb[id] == null && prevTomb[id] == null) { regressed = true; break; }
    // Tombstones: explicit user deletes, plus ones this device already carries.
    const tomb = {};
    for (const src of [prevTomb, userTomb]) {
      for (const id of Object.keys(src)) {
        let dt = Number(src[id]);
        if (!Number.isFinite(dt) || now - dt > TOMBSTONE_TTL) continue;
        if (src === prevTomb && userTomb[id] == null && live[id]) continue; // re-created
        if (src === userTomb && prevIdx[id]) dt = Math.max(dt, prevIdx[id][1] + 1); // beats the version the user deleted
        if (tomb[id] == null || dt > tomb[id]) tomb[id] = dt;
      }
    }
    for (const id of Object.keys(tomb)) {
      if (live[id]) {
        if (live[id].t > tomb[id]) { delete tomb[id]; continue; }
        delete live[id]; delete idx[id]; delete next.pf[id];
      }
      if (prevTomb[id] !== tomb[id]) changes++;
    }
    doc.c[coll] = live; doc.x[coll] = tomb;
    next.c[coll] = idx; next.x[coll] = tomb;
  }
  const prefs = st.prefs && typeof st.prefs === 'object' ? st.prefs : {};
  for (const k of Object.keys(prefs)) {
    const v = prefs[k];
    if (v === undefined) continue;
    const h = hashOf(v); const pm = m.p[k];
    let t;
    if (pm && pm[0] === h) t = pm[1];
    else if (!pm && (first || k in PREF_DEFAULTS) && hashOf(PREF_DEFAULTS[k]) === h) t = 0;
    else if (first) t = 1;
    else { t = restoreAt != null ? restoredAt('p', null, k) : stamp(pm ? pm[1] : null); changes++; if (pm && t < pm[1]) regressed = true; }
    doc.p[k] = { t, v }; next.p[k] = [h, t];
  }
  const sc = scalarsOf(st);
  for (const k of SCALARS) {
    const v = sc[k];
    const h = hashOf(v); const pm = m.s[k];
    let t;
    if (pm && pm[0] === h) t = pm[1];
    else if (!pm && hashOf(SCALAR_DEFAULTS[k]) === h) t = 0;
    else if (first) t = 1;
    else { t = restoreAt != null ? restoredAt('s', null, k) : stamp(pm ? pm[1] : null); changes++; if (pm && t < pm[1]) regressed = true; }
    doc.s[k] = { t, v }; next.s[k] = [h, t];
  }
  next.hlc = hlc;
  // A fresh token per regression, so a sync that started before it never clears it.
  next.needMerge = regressed ? now + Math.random() : (m.needMerge || 0);
  return { doc, meta: next, changes, regressed };
}

function indexProgram(e, h, pm, pf, first, stamp, undeleteAt, now) {
  const f = flatOf(e);
  if (!f) {
    let t, changed = false;
    if (pm && pm[0] === h) t = pm[1];
    else if (undeleteAt != null) { t = Math.max(now, undeleteAt + 1); changed = true; }
    else if (first) t = 1;
    else { t = stamp(pm ? pm[1] : null); changed = true; }
    return { rec: { t, h, d: e }, pf: null, changed };
  }
  if (pm && pm[0] === h && pf) {
    const ft = {};
    let t = 0;
    for (const p of Object.keys(pf)) { ft[p] = pf[p][1]; if (pf[p][1] > t) t = pf[p][1]; }
    return { rec: { t: Math.max(t, pm[1]), h, d: e, ft }, pf, changed: false };
  }
  const npf = {}, ft = {};
  let changed = false;
  for (const path of Object.keys(f.fh)) {
    const fh = f.fh[path];
    const pv = pf && pf[path];
    let t;
    if (pv && pv[0] === fh) t = pv[1];
    else if (pm && !pf && pm[0] === h) t = pm[1];                     // legacy meta, unchanged
    else if (undeleteAt != null) { t = Math.max(now, undeleteAt + 1); changed = true; }
    else if (first || (pm && !pf && pm[1] <= 1)) t = 1;                 // never tracked
    else { t = stamp(pv ? pv[1] : (pm ? pm[1] : null)); changed = true; }
    npf[path] = [fh, t]; ft[path] = t;
  }
  if (pf) {
    for (const path of Object.keys(pf)) {
      if (path in f.fh || elementGone(path, f)) continue;
      const pv = pf[path];
      const t = pv[0] === '' ? pv[1] : stamp(pv[1]);                    // a field that was removed
      if (pv[0] !== '') changed = true;
      if (now - t > TOMBSTONE_TTL) continue;
      npf[path] = ['', t]; ft[path] = t;
    }
  }
  let t = 0;
  for (const v of Object.values(ft)) if (v > t) t = v;
  return { rec: { t, h, d: e, ft }, pf: npf, changed };
}

/**
 * Last edit time of every synced record on this device (from the edit index),
 * for backups: { c: { <collection>: { <id>: t } }, p: { <pref>: t }, s: { <scalar>: t } }.
 */
export function recordTimes() {
  const m = trackerMeta();
  const out = { c: {}, p: {}, s: {} };
  for (const coll of COLLECTIONS) { out.c[coll] = {}; for (const [id, e] of Object.entries((m.c && m.c[coll]) || {})) out.c[coll][id] = e[1]; }
  for (const [k, e] of Object.entries(m.p || {})) out.p[k] = e[1];
  for (const [k, e] of Object.entries(m.s || {})) out.s[k] = e[1];
  return out;
}

/** Newest timestamp anywhere in a doc (records, field times, tombstones, prefs, scalars). */
export function maxDocT(doc) {
  let t = 0;
  for (const coll of COLLECTIONS) {
    for (const e of Object.values((doc.c && doc.c[coll]) || {})) if (e && e.t > t) t = e.t;
    for (const v of Object.values((doc.x && doc.x[coll]) || {})) if (v > t) t = v;
  }
  for (const part of ['p', 's']) for (const e of Object.values(doc[part] || {})) if (e && e.t > t) t = e.t;
  return t;
}

// metaFromDoc in slices (for big docs, before they are applied).
async function metaFromDocSliced(doc) {
  const slice = slicer();
  const m = { v: 2, init: true, c: {}, pf: {}, p: {}, s: {}, x: {} };
  for (const coll of COLLECTIONS) {
    m.c[coll] = {};
    for (const [id, e] of Object.entries((doc.c && doc.c[coll]) || {})) {
      if (coll === 'programs') { const one = metaFromDoc({ c: { programs: { [id]: e } } }); m.c.programs[id] = one.c.programs[id]; if (one.pf[id]) m.pf[id] = one.pf[id]; continue; }
      m.c[coll][id] = [recHash(e), e.t];
      const y = slice(); if (y) await y;
    }
    m.x[coll] = { ...((doc.x && doc.x[coll]) || {}) };
  }
  for (const [k, e] of Object.entries(doc.p || {})) m.p[k] = [hashOf(e.v), e.t];
  for (const [k, e] of Object.entries(doc.s || {})) m.s[k] = [hashOf(e.v), e.t];
  return m;
}

/** Meta that exactly describes a doc (after we applied it locally). */
export function metaFromDoc(doc, state) {
  const m = { v: 2, init: true, c: {}, pf: {}, p: {}, s: {}, x: {}, restoreId: state && state.syncRestore ? state.syncRestore.id : undefined };
  for (const coll of COLLECTIONS) {
    m.c[coll] = {};
    for (const [id, e] of Object.entries((doc.c && doc.c[coll]) || {})) {
      m.c[coll][id] = [recHash(e), e.t];
      if (coll === 'programs' && e.ft) {
        const f = flatOf(e.d);
        if (!f) continue;
        const pf = {};
        for (const path of Object.keys(e.ft)) {
          if (path in f.fh) pf[path] = [f.fh[path], e.ft[path]];
          else if (!elementGone(path, f)) pf[path] = ['', e.ft[path]];
        }
        for (const path of Object.keys(f.fh)) if (!pf[path]) pf[path] = [f.fh[path], e.t];
        m.pf[id] = pf;
      }
    }
    m.x[coll] = { ...((doc.x && doc.x[coll]) || {}) };
  }
  for (const [k, e] of Object.entries(doc.p || {})) m.p[k] = [hashOf(e.v), e.t];
  for (const [k, e] of Object.entries(doc.s || {})) m.s[k] = [hashOf(e.v), e.t];
  return m;
}

// ───────────────────────────── merge ─────────────────────────────

function newer(a, b) {
  if (!a) return b; if (!b) return a;
  if (a.t !== b.t) return a.t > b.t ? a : b;
  // Never let an in-progress copy beat a completed one (defensive; docs only carry completed sessions).
  const ac = a.d && a.d.completedAt, bc = b.d && b.d.completedAt;
  if (!!ac !== !!bc) return ac ? a : b;
  return recHash(a) >= recHash(b) ? a : b;
}

/** Merge two docs. Pure, commutative, idempotent. */
export function mergeDocs(a, b) {
  if (!a) return b; if (!b) return a;
  const out = { v: DOC_V, app: 'liftlog', c: {}, x: {}, p: {}, s: {}, at: Math.max(a.at || 0, b.at || 0), by: (a.at || 0) >= (b.at || 0) ? a.by : b.by };
  for (const coll of COLLECTIONS) {
    const ca = (a.c && a.c[coll]) || {}, cb = (b.c && b.c[coll]) || {};
    const xa = (a.x && a.x[coll]) || {}, xb = (b.x && b.x[coll]) || {};
    const live = {}, tomb = {};
    const visit = (id) => {
      if (id in live || id in tomb) return;
      const ea = ca[id], eb = cb[id];
      const win = coll === 'programs' && ea && eb ? mergeProgram(ea, eb) : newer(ea, eb);
      const tt = Math.max(xa[id] ?? -Infinity, xb[id] ?? -Infinity);
      if (win && !(tt >= win.t)) live[id] = win;
      else if (tt > -Infinity) tomb[id] = tt;
    };
    for (const m of [ca, cb, xa, xb]) for (const id of Object.keys(m)) visit(id);
    // One weigh-in / measurement per date: the newest wins, the others are retired.
    if (coll === 'bodyEntries' || coll === 'measurements') {
      const byDate = {};
      for (const id of Object.keys(live)) {
        const e = live[id]; const k = e.d && e.d.date; if (!k) continue;
        const cur = byDate[k];
        if (!cur) { byDate[k] = id; continue; }
        const w = newer(live[cur], e) === e ? id : cur;
        const lose = w === id ? cur : id;
        tomb[lose] = Math.max(tomb[lose] ?? -Infinity, live[lose].t);
        delete live[lose];
        byDate[k] = w;
      }
    }
    out.c[coll] = live; out.x[coll] = tomb;
  }
  for (const part of ['p', 's']) {
    const pa = a[part] || {}, pb = b[part] || {};
    for (const k of new Set([...Object.keys(pa), ...Object.keys(pb)])) out[part][k] = newer(pa[k], pb[k]);
  }
  // Once onboarded anywhere, onboarded everywhere.
  if ((onboarded(a) || onboarded(b)) && out.s.welcomed) out.s.welcomed = { ...out.s.welcomed, v: true };
  return out;
}
const onboarded = (d) => !!(d.s && d.s.welcomed && d.s.welcomed.v);

// ───────────────────────────── join policy ─────────────────────────────

const normName = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
/**
 * Content hash of a program ignoring its ids (and its origin stamp). The store
 * stamps new programs with origin.h = this hash, so a program nobody has
 * edited yet (an onboarding starter) can be recognised later.
 */
export function programContentHash(p) {
  if (!p || typeof p !== 'object') return '';
  const { id: _id, origin: _o, ...rest } = p;
  const days = Array.isArray(p.days) ? p.days.map((d) => {
    if (!d || typeof d !== 'object') return d;
    const { id: _d, ...dr } = d;
    return { ...dr, exercises: Array.isArray(d.exercises) ? d.exercises.map((e) => { if (!e || typeof e !== 'object') return e; const { id: _e, ...er } = e; return er; }) : d.exercises };
  }) : p.days;
  return hashOf({ ...rest, days });
}
/** A program created by onboarding / a template that was never edited. */
export function isUntouchedProgram(p) {
  return !!(p && p.origin && p.origin.h && programContentHash(p) === p.origin.h);
}
function cloneDoc(d) {
  const o = { ...d, c: {}, x: {}, p: { ...(d.p || {}) }, s: { ...(d.s || {}) } };
  for (const coll of COLLECTIONS) { o.c[coll] = { ...((d.c && d.c[coll]) || {}) }; o.x[coll] = { ...((d.x && d.x[coll]) || {}) }; }
  return o;
}
function remapSession(s, from, to) {
  const fromDays = from.days || [], toDays = to.days || [];
  const fromDay = fromDays.find((d) => d.id === s.dayId);
  const toDay = fromDay ? (toDays.find((d) => normName(d.name) === normName(fromDay.name)) || toDays[fromDays.indexOf(fromDay)]) : null;
  const peMap = {};
  if (fromDay && toDay) {
    const used = new Set();
    for (const pe of fromDay.exercises || []) {
      const m = (toDay.exercises || []).find((x) => !used.has(x.id) && x.exerciseId === pe.exerciseId);
      if (m) { used.add(m.id); peMap[pe.id] = m.id; }
    }
  }
  const out = {
    ...s, programId: to.id, dayId: toDay ? toDay.id : s.dayId,
    sets: (s.sets || []).map((st) => (st && peMap[st.peId] != null ? { ...st, peId: peMap[st.peId] } : st)),
  };
  if (Array.isArray(s.exercises)) out.exercises = s.exercises.map((sl) => (sl && peMap[sl.id] != null ? { ...sl, id: peMap[sl.id] } : sl));
  return out;
}
/**
 * Resolve a first sync between a joining device (local) and an existing vault
 * (remote). prefer 'remote' (default): the vault's prefs, name, profile and
 * active program win; 'local': this device's win. Programs with the same name
 * on both sides are folded into one (the copy with more workouts behind it,
 * else the preferred side); workouts that pointed at the dropped copy move to
 * the kept one. Pure.
 */
export function applyJoinPolicy(local, remote, prefer = 'remote', now = Date.now()) {
  const L = cloneDoc(local), R = cloneDoc(remote);
  for (const part of ['p', 's']) {
    for (const k of Object.keys(L[part])) {
      const le = L[part][k], re = R[part][k];
      if (!le) continue;
      if (prefer === 'remote') {
        if (re && (part === 'p' || re.t > 0 || !isUnset(re.v))) L[part][k] = { ...le, t: -1 };
      } else if (part === 'p' || le.t > 0 || !isUnset(le.v)) {
        L[part][k] = { ...le, t: Math.max(now, (re ? re.t : 0) + 1) };
      }
    }
  }
  const refs = {};
  for (const D of [L, R]) for (const e of Object.values(D.c.sessions)) { const pid = e.d && e.d.programId; if (pid != null) refs[pid] = (refs[pid] || 0) + 1; }
  const remoteByName = new Map();
  for (const [id, e] of Object.entries(R.c.programs)) { const k = normName(e.d && e.d.name); if (k && !remoteByName.has(k)) remoteByName.set(k, id); }
  const folds = [];
  const taken = new Set();
  for (const [id, e] of Object.entries(L.c.programs)) {
    if (R.c.programs[id]) continue;
    const rid = remoteByName.get(normName(e.d && e.d.name));
    if (!rid || taken.has(rid)) continue;
    taken.add(rid);
    const lRefs = refs[e.d.id] || 0, rRefs = refs[R.c.programs[rid].d.id] || 0;
    const keepRemote = rRefs > lRefs || (rRefs === lRefs && prefer === 'remote');
    folds.push(keepRemote ? { lose: id, loseDoc: L, keep: R.c.programs[rid] } : { lose: rid, loseDoc: R, keep: e });
  }
  // An untouched program this device made during onboarding (no workouts, not
  // edited since it was created) is dropped when the vault already has
  // programs: joining never adds a stray starter to every device.
  const remoteProgs = Object.values(R.c.programs).filter((e) => e && e.d);
  const folded = new Set(folds.map((f) => f.lose));
  if (remoteProgs.length) {
    for (const [id, e] of Object.entries(L.c.programs)) {
      if (R.c.programs[id] || folded.has(id) || !e.d || refs[e.d.id] || !isUntouchedProgram(e.d)) continue;
      delete L.c.programs[id];
      L.x.programs[id] = Math.max(now, e.t + 1);
      const a = L.s.activeProgramId;
      if (a && a.v === e.d.id) {
        const ra = R.s.activeProgramId;
        const v = ra && ra.v != null && R.c.programs[String(ra.v)] ? ra.v : remoteProgs[0].d.id;
        L.s.activeProgramId = { ...a, v, t: -1 };
      }
    }
  }
  for (const { lose, loseDoc, keep } of folds) {
    const loser = loseDoc.c.programs[lose];
    for (const D of [L, R]) {
      for (const [sid, se] of Object.entries(D.c.sessions)) {
        if (!se.d || se.d.programId !== loser.d.id) continue;
        const d = remapSession(se.d, loser.d, keep.d);
        D.c.sessions[sid] = { t: Math.max(now, se.t + 1), h: hashEntity(d), d };
      }
      const a = D.s.activeProgramId;
      if (a && a.v === loser.d.id) D.s.activeProgramId = { ...a, v: keep.d.id };
    }
    delete loseDoc.c.programs[lose];
    loseDoc.x.programs[lose] = Math.max(now, loser.t + 1);
  }
  return { local: L, remote: R };
}

// ───────────────────────────── doc → state ─────────────────────────────

const localDateStr = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/** PRs, same rules as the store (prs.js: effective load, working sets only). */
export function computePRs(sessions, exercises, bodyEntries, profile) {
  return prsOf({ sessions, exercises, bodyEntries, profile }, sessions);
}
// Pre-compute each workout's bests in slices before a big doc is applied.
async function warmBests(doc) {
  const st = readLocalState() || {};
  await warmPRs(st, Object.values((doc.c && doc.c.sessions) || {}).map((e) => e.d).filter((d) => d && typeof d === 'object'), slicer());
}

function orderLike(prevArr, docMap) {
  const out = []; const seen = new Set();
  for (const e of prevArr || []) {
    const id = e && e.id != null ? String(e.id) : null;
    if (id && docMap[id] && !seen.has(id)) { out.push(docMap[id].d); seen.add(id); }
  }
  for (const [id, e] of Object.entries(docMap)) if (!seen.has(id)) out.push(e.d);
  return out;
}
const byDateAsc = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

/** Project a doc onto the previous app state → next app state. Pure. Keeps ephemeral + device-only fields. */
const sameList = (a, b) => Array.isArray(a) && a.length === b.length && a.every((x, i) => x === b[i]);
const sameValue = (a, b) => a === b || (a != null && b != null && typeof a === 'object' && typeof b === 'object' && hashOf(a) === hashOf(b));

/**
 * Project a doc onto the previous app state → next app state. Pure. Keeps
 * ephemeral + device-only fields, and keeps the previous array / object
 * whenever its content did not change (so screens don't recompute analytics
 * over the whole history because one setting arrived).
 */
export function docToState(prev, doc) {
  const c = doc.c || {};
  for (const coll of COLLECTIONS) for (const e of Object.values(c[coll] || {})) if (e.d && typeof e.d === 'object' && e.h != null && !memo.has(e.d)) memo.set(e.d, e.h);
  const live = Object.values(c.sessions || {}).map((e) => e.d);
  const liveIds = new Set(live.map((s) => String(s.id)));
  const localOpen = (prev.sessions || []).filter((s) => s && !s.completedAt && !liveIds.has(String(s.id)));
  let sessions = [...live, ...localOpen].sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
  if (sameList(prev.sessions, sessions)) sessions = prev.sessions;
  let exercises = orderLike(prev.exercises, c.exercises || {});
  if (sameList(prev.exercises, exercises)) exercises = prev.exercises;
  let programs = orderLike(prev.programs, c.programs || {});
  if (sameList(prev.programs, programs)) programs = prev.programs;
  let bodyEntries = Object.values(c.bodyEntries || {}).map((e) => e.d).sort(byDateAsc);
  if (sameList(prev.bodyEntries, bodyEntries)) bodyEntries = prev.bodyEntries;
  let measurements = Object.values(c.measurements || {}).map((e) => e.d).sort(byDateAsc);
  if (sameList(prev.measurements, measurements)) measurements = prev.measurements;
  const localPhotos = (prev.photos || []).filter((p) => p && p.dataUrl && !(c.photos && c.photos[String(p.id)])); // never synced (see itemsOf)
  let photos = [...Object.values(c.photos || {}).map((e) => e.d), ...localPhotos].sort((a, b) => byDateAsc(a, b) || ((a.createdAt || '') < (b.createdAt || '') ? -1 : (a.createdAt || '') > (b.createdAt || '') ? 1 : 0));
  if (sameList(prev.photos, photos)) photos = prev.photos;
  const prevPrefs = prev.prefs || {};
  let prefs = prevPrefs;
  for (const [k, e] of Object.entries(doc.p || {})) {
    if (sameValue(prevPrefs[k], e.v)) continue;
    if (prefs === prevPrefs) prefs = { ...prevPrefs };
    prefs[k] = e.v;
  }
  const s = doc.s || {};
  const apid = s.activeProgramId ? s.activeProgramId.v : prev.activeProgramId;
  let deleted = {};
  for (const coll of COLLECTIONS) deleted[coll] = { ...((doc.x && doc.x[coll]) || {}) };
  if (prev.deleted && hashOf(prev.deleted) === hashOf(deleted)) deleted = prev.deleted;
  // PRs only need rebuilding when the completed workouts, exercises or body weights changed.
  const samePRs = sessions === prev.sessions && exercises === prev.exercises && bodyEntries === prev.bodyEntries && Array.isArray(prev.prs);
  const next = {
    ...prev,
    sessions,
    exercises,
    programs,
    bodyEntries,
    measurements,
    photos,
    prefs,
    deleted,
    activeProgramId: programs.some((p) => p.id === apid) ? apid : (programs.some((p) => p.id === prev.activeProgramId) ? prev.activeProgramId : (programs[0] ? programs[0].id : null)),
    welcomed: !!(prev.welcomed || (s.welcomed && s.welcomed.v)),
    prs: samePRs ? prev.prs : prsOf({ ...prev, sessions, exercises, bodyEntries }, sessions),
  };
  if (s.profile && !sameValue(prev.profile ?? null, s.profile.v ?? null)) next.profile = s.profile.v ?? null;
  if (s.userName && ((prev.user && prev.user.name) || '') !== (s.userName.v || '')) next.user = { ...(prev.user || {}), name: s.userName.v || '' };
  return next;
}

export function docCounts(doc) {
  const n = (coll) => Object.keys((doc && doc.c && doc.c[coll]) || {}).length;
  return { sessions: n('sessions'), programs: n('programs'), bodyEntries: n('bodyEntries'), measurements: n('measurements'), photos: n('photos') };
}
function docSummary(doc) {
  const s = doc.s || {}, p = doc.p || {};
  const ap = s.activeProgramId && s.activeProgramId.v;
  const prog = ap != null && doc.c && doc.c.programs && doc.c.programs[String(ap)];
  return {
    name: (s.userName && s.userName.v) || '',
    units: (p.units && p.units.v) || 'lb',
    program: prog && prog.d ? prog.d.name || '' : '',
    programs: Object.values((doc.c && doc.c.programs) || {}).map((e) => (e.d && e.d.name) || '').filter(Boolean),
  };
}

// ───────────────────────────── edit tracker (stateful) ─────────────────────────────

const T = { meta: null, dirty: false, saveT: null };
function trackerMeta() { if (!T.meta) T.meta = upgradeMeta(lsGet(K.meta, null)); return T.meta; }
function setTrackerMeta(m) {
  T.meta = m; T.dirty = true;
  if (!T.saveT) T.saveT = setTimeout(flushMeta, 800);
}
function flushMeta() {
  clearTimeout(T.saveT); T.saveT = null;
  if (T.dirty && T.meta) { lsSet(K.meta, T.meta); T.dirty = false; }
}
/** Forget all edit history (used by "Reset all data"). */
export function resetSyncTracking() { clearTimeout(T.saveT); T.saveT = null; T.meta = emptyMeta(); T.dirty = false; lsDel(K.meta); }

// Hash every record not seen before, in slices, so a cold start with years of
// history never blocks the main thread.
async function warmHashes(state) {
  if (!state) return;
  const slice = slicer();
  for (const coll of COLLECTIONS) {
    const arr = Array.isArray(state[coll]) ? state[coll] : [];
    for (const e of arr) {
      if (!e || typeof e !== 'object' || memo.has(e)) continue;
      if (coll === 'sessions' && !e.completedAt) continue;
      hashEntity(e);
      if (coll === 'programs') flatOf(e);
      const y = slice(); if (y) await y;
    }
  }
}
function indexNow(state, now = Date.now(), dev) {
  const r = indexState(state, trackerMeta(), now, dev || currentDeviceId());
  setTrackerMeta(r.meta);
  return r;
}

let obsNext = null, obsRunning = false;
/**
 * Record edits as they happen: call with the latest app state after every
 * change (useSyncBridge does). Runs whether sync is on or off. Changes to
 * synced data schedule a sync (see notifyLocalChange).
 */
export function observeLocalState(state, now = Date.now()) {
  if (!state) return Promise.resolve();
  obsNext = { state, now: obsNext ? Math.min(obsNext.now, now) : now };
  if (obsRunning) return Promise.resolve();
  obsRunning = true;
  return (async () => {
    try {
      while (obsNext) {
        const { state: s } = obsNext;
        await warmHashes(s);
        if (obsNext.state !== s) continue; // a newer state arrived while hashing
        const at = obsNext.now; obsNext = null;
        const r = indexNow(s, at);
        if (r.changes) notifyLocalChange();
      }
    } catch (e) { console.warn('[sync] tracking failed', e); } finally { obsRunning = false; }
  })();
}

// ───────────────────────────── transport ─────────────────────────────

async function api(path, init = {}) {
  let res;
  try {
    res = await doFetch(syncBase() + path, { cache: 'no-store', ...init });
  } catch (e) {
    throw syncError('offline', 'Can’t reach the sync server');
  }
  return res;
}
async function getRemote(vaultId, etag) {
  const res = await api(`/sync/${vaultId}`, { headers: etag ? { 'If-None-Match': etag } : {} });
  if (res.status === 304) return { status: 304, etag };
  if (res.status === 404) return { status: 404 };
  if (res.status === 410) return { status: 410 };
  if (res.status === 200) return { status: 200, etag: res.headers.get('ETag'), bytes: new Uint8Array(await res.arrayBuffer()) };
  throw httpError(res);
}
async function putRemote(vaultId, bytes, etag) {
  const res = await api(`/sync/${vaultId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream', ...(etag ? { 'If-Match': etag } : { 'If-None-Match': '*' }) },
    body: bytes,
  });
  if (res.status === 412) return { conflict: true };
  if (res.status === 410) return { revoked: true };
  if (res.status === 200 || res.status === 201) return { etag: res.headers.get('ETag') || (await res.json()).etag };
  throw httpError(res);
}
function httpError(res) {
  if (res.status === 413) return syncError('too-large', 'Your data is larger than the sync limit (5 MB)');
  if (res.status === 429) return syncError('rate', 'Syncing too often — will retry shortly');
  if (res.status === 404 || res.status === 405 || res.status === 502 || res.status === 503) return syncError('unavailable', 'Sync server unavailable');
  return syncError('http', `Sync failed (HTTP ${res.status})`);
}
/** True when this install has a reachable sync server. */
export async function probeSyncServer() {
  try {
    const r = await api('/health');
    if (!r.ok) return false;
    const j = await r.json().catch(() => null);
    return !!(j && j.ok);
  } catch (_) { return false; }
}

// ───────────────────────────── engine ─────────────────────────────

const hosts = [];               // stack of { getState, applyMergedState, onJoinLink }
const host = () => hosts[hosts.length - 1] || null;
const listeners = new Set();
let status = null;
let running = null;
let rerun = false;
let debounceT = null;
let pollT = null;
let wired = false;
let lastPullAt = 0;
let pendingLive = false;

function cfg() { return lsGet(K.cfg, null); }
function setCfg(patch) { const c = { ...(cfg() || {}), ...patch }; lsSet(K.cfg, c); return c; }
function currentDeviceId() {
  const c = cfg();
  if (c && c.deviceId) return c.deviceId;
  const id = hex(globalThis.crypto.getRandomValues(new Uint8Array(8)));
  setCfg({ deviceId: id });
  return id;
}

function computeStatus(extra = {}) {
  const c = cfg();
  const on = !!(c && c.code);
  return {
    enabled: on,
    state: !on ? 'off' : 'idle',
    lastSyncAt: c ? c.lastSyncAt || null : null,
    error: on ? c.lastError || null : null,
    errorCode: on ? c.lastErrorCode || null : null,
    canRepair: !!(on && c.unreadable),
    notice: !on && c && c.revokedAt ? 'revoked' : null,
    pending: false,
    ...extra,
  };
}
function emit(patch) {
  status = { ...(status || computeStatus()), ...patch };
  for (const fn of listeners) { try { fn(status); } catch (_) { /* ignore */ } }
}
export function getSyncStatus() { if (!status) status = computeStatus(); return status; }
export function subscribeSyncStatus(fn) { listeners.add(fn); fn(getSyncStatus()); return () => listeners.delete(fn); }
export function getSyncCode() { const c = cfg(); return c && c.code ? c.code : null; }
/** Clear the "sync was turned off from another device" notice. */
export function dismissSyncNotice() { const c = cfg(); if (c && c.revokedAt) { const { revokedAt, ...rest } = c; lsSet(K.cfg, rest); } status = computeStatus(); emit({}); }

/**
 * initSync({ getState, applyMergedState, onJoinLink }) → dispose()
 *   getState()               latest app state object
 *   applyMergedState(fn)     apply `fn(prevState) → nextState` atomically (e.g. setState(fn))
 *   onJoinLink(code)         optional: app was opened via a #sync= link (route to settings/sync)
 * Safe to call more than once (the most recent registration wins; dispose restores the previous).
 */
export function initSync(h) {
  hosts.push(h);
  if (typeof window !== 'undefined' && !wired) {
    wired = true;
    const pull = () => {
      if (document.hidden) return;
      // Coming back to the app is a cheap 304 most of the time; still, not more than every 15 s.
      if (Date.now() - lastPullAt < FOCUS_MIN_MS) return;
      syncNow({ reason: 'focus' });
    };
    window.addEventListener('focus', pull);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) pull();
      else flushOnHide();
    });
    window.addEventListener('pagehide', () => { flushMeta(); });
    window.addEventListener('online', () => syncNow({ reason: 'online' }));
    window.addEventListener('offline', () => getSyncStatus().enabled && emit({ state: 'offline' }));
    pollT = setInterval(() => { if (!document.hidden) syncNow({ reason: 'poll' }); }, POLL_MS);
    wirePushVisibility();
    const code = readSyncLinkFromLocation();
    if (code) { try { sessionStorage.setItem('liftlog.syncJoin', code); } catch (_) { /* ignore */ } }
    // A sync link opened while the app is already running (same tab).
    window.addEventListener('hashchange', () => {
      const c = readSyncLinkFromLocation();
      if (!c) return;
      try { sessionStorage.setItem('liftlog.syncJoin', c); } catch (_) { /* ignore */ }
      const hh = host();
      if (hh && hh.onJoinLink) hh.onJoinLink(c);
      try { window.dispatchEvent(new CustomEvent('liftlog-sync-join', { detail: c })); } catch (_) { /* old browsers */ }
    });
  }
  let pendingJoin = null;
  try { pendingJoin = sessionStorage.getItem('liftlog.syncJoin'); } catch (_) { /* node */ }
  if (pendingJoin && h.onJoinLink) setTimeout(() => h.onJoinLink(pendingJoin), 0);
  if (getSyncStatus().enabled) setTimeout(() => syncNow({ reason: 'start' }), 800);
  return () => { const i = hosts.lastIndexOf(h); if (i >= 0) hosts.splice(i, 1); };
}
/** A join link that arrived on launch (consumed by SyncSettings). */
export function takePendingJoin() {
  try { const c = sessionStorage.getItem('liftlog.syncJoin'); sessionStorage.removeItem('liftlog.syncJoin'); return c; } catch (_) { return null; }
}
export function peekPendingJoin() { try { return sessionStorage.getItem('liftlog.syncJoin'); } catch (_) { return null; } }

function readLocalState() {
  const h = host();
  if (h && h.getState) return h.getState();
  return lsGet(K.state, null);
}
function workoutLive() {
  const s = readLocalState();
  return !!(s && s.activeSessionId && Array.isArray(s.sessions) && s.sessions.some((x) => x && x.id === s.activeSessionId && !x.completedAt));
}

/**
 * A synced record changed locally. Debounced; while a workout is live the sync
 * waits for the app being hidden, the 5-minute poll or the workout finishing.
 */
export function notifyLocalChange() {
  if (!getSyncStatus().enabled) return;
  clearTimeout(debounceT); debounceT = null;
  emit({ pending: true });
  if (workoutLive()) { pendingLive = true; return; }
  debounceT = setTimeout(() => syncNow({ reason: 'change' }), DEBOUNCE_MS);
}
function flushOnHide() {
  flushMeta();
  if (debounceT || pendingLive) { clearTimeout(debounceT); debounceT = null; syncNow({ reason: 'hide' }); }
}

/**
 * Pull → merge → apply → push. Serialized; returns the final status.
 * opts.force: always hit the network (manual "Sync now").
 */
export function syncNow(opts = {}) {
  if (!getSyncStatus().enabled) return Promise.resolve(getSyncStatus());
  if (running) { rerun = true; return running; }
  clearTimeout(debounceT); debounceT = null; pendingLive = false;
  running = (async () => {
    try { await runOnce(opts); } finally {
      running = null;
      flushMeta();
      if (rerun) { rerun = false; setTimeout(() => syncNow({ reason: 'rerun' }), 0); }
    }
    return getSyncStatus();
  })();
  return running;
}

const UNREADABLE = 'The synced copy can’t be read — it may be damaged. Replace it with this device’s data to keep syncing.';

async function runOnce(opts) {
  const c = cfg();
  if (!c || !c.code) return;
  const state = readLocalState();
  if (!state) return;
  if (state.hydrated === false) { deferredRun = true; return; } // still loading the full history
  const dev = c.deviceId || currentDeviceId();
  // Index first, even offline: local edits and deletes are recorded either way.
  await warmHashes(state);
  let { doc: local } = indexNow(state, Date.now(), dev);
  if (typeof navigator !== 'undefined' && navigator.onLine === false) { emit({ state: 'offline', pending: true }); return; }
  emit({ state: 'syncing' });
  try {
    const keys = await deriveKeys(c.code);
    const localHash = docHash(local);
    const joining = c.joining || null;
    // This device's copy lost records or went back in time (restore, undo,
    // reset) since the last sync: it must be merged with the synced copy, so a
    // 304 is only trusted when the last-synced doc is at hand to merge against.
    const needMerge = trackerMeta().needMerge || 0;
    // Nothing changed locally and we pulled recently → skip the network.
    if (!opts.force && !joining && !needMerge && localHash === c.lastHash && c.etag && Date.now() - lastPullAt < 20000 && opts.reason === 'change') {
      emit({ state: 'idle', pending: false });
      return;
    }
    let etag = c.etag || null;
    const cached = () => (lastDoc && lastDoc.code === c.code && etag && lastDoc.etag === etag ? lastDoc : null);
    for (let attempt = 0; attempt < 5; attempt++) {
      // A brand-new vault (random key) can't exist yet: skip the GET (and its 404).
      const r = c.fresh && attempt === 0 ? { status: 404 } : await getRemote(keys.vaultId, needMerge && !cached() ? null : etag);
      lastPullAt = Date.now();
      if (!sameVault(c)) return; // sync was turned off / switched to another code meanwhile
      if (r.status === 410) { revokedHere(); return; }
      let merged = local;
      let remoteHash = null;
      if (r.status === 304) {
        const base = cached();
        if (base) {
          // Merge against the last-synced doc: a restore on this device never
          // drops (or rolls back) what the synced copy holds.
          remoteHash = base.hash;
          merged = mergeDocs(local, base.doc);
          await yieldMain();
          if (docHash(merged) !== localHash) {
            await warmBests(merged);
            const pre = await metaFromDocSliced(merged);
            if (!sameVault(c)) return;
            applyDoc(merged, dev, null, attempt === 0 ? state : null, pre);
            await yieldMain();
          }
          local = merged;
        } else remoteHash = c.lastHash;
      } else if (r.status === 404) {
        if (joining) throw syncError('missing', 'No synced data found for this code');
        etag = null;
      } else {
        etag = r.etag;
        let remote;
        try {
          remote = await decryptDoc(r.bytes, keys, { reuse: reuseFrom(local) });
        } catch (e) {
          if (e.code === 'newer') throw e;
          setCfg({ unreadable: { etag: r.etag, at: Date.now() } });
          throw syncError('unreadable', UNREADABLE);
        }
        remoteHash = docHash(remote);
        await yieldMain();
        let mine = local;
        if (joining) { const j = applyJoinPolicy(local, remote, joining.prefer || 'remote'); mine = j.local; remote = j.remote; }
        merged = mergeDocs(mine, remote);
        await yieldMain();
        if (docHash(merged) !== localHash) {
          await warmBests(merged);
          const pre = await metaFromDocSliced(merged);
          if (!sameVault(c)) return;
          applyDoc(merged, dev, joining ? joining.prefer || 'remote' : null, attempt === 0 ? state : null, pre);
          await yieldMain();
        }
        local = merged;
      }
      const mergedHash = docHash(merged);
      const done = { etag, lastHash: mergedHash, lastSyncAt: Date.now(), lastError: null, lastErrorCode: null, unreadable: null, joining: null };
      if (mergedHash === remoteHash) {
        if (!sameVault(c)) return;
        setCfg(done);
        synced(c, etag, merged, mergedHash, needMerge);
        emit({ state: 'idle', pending: false, lastSyncAt: Date.now(), error: null, errorCode: null, canRepair: false });
        afterSynced();
        return;
      }
      const bytes = await encryptDoc({ ...merged, at: Date.now(), by: dev }, keys);
      if (!sameVault(c)) return;
      const put = await putRemote(keys.vaultId, bytes, etag);
      if (!sameVault(c)) return;
      if (put.revoked) { revokedHere(); return; }
      if (put.conflict) { etag = null; continue; } // someone else wrote — pull again and re-merge
      setCfg({ ...done, etag: put.etag, lastBytes: bytes.length, fresh: false });
      synced(c, put.etag, merged, mergedHash, needMerge);
      emit({ state: 'idle', pending: false, lastSyncAt: Date.now(), error: null, errorCode: null, canRepair: false });
      afterSynced();
      return;
    }
    throw syncError('conflict', 'Too many simultaneous changes — will retry');
  } catch (e) {
    const offline = e.code === 'offline';
    if (!offline) setCfg({ lastError: e.message, lastErrorCode: e.code || 'error' });
    emit({ state: offline ? 'offline' : 'error', error: offline ? null : e.message, errorCode: offline ? null : (e.code || 'error'), canRepair: !!(cfg() || {}).unreadable, pending: true });
    if (!offline) console.warn('[sync]', e.message);
  }
}
// The last doc this device and the vault agreed on (in memory), for merging on a 304.
let lastDoc = null;
let deferredRun = false; // a sync asked for before the store finished loading
const sameVault = (c) => { const now = cfg(); return !!(now && now.code && now.code === c.code); };
function synced(c, etag, doc, hash, needMerge) {
  lastDoc = etag ? { code: c.code, etag, doc, hash } : null;
  const m = trackerMeta();
  if (needMerge && m.needMerge === needMerge) setTrackerMeta({ ...m, needMerge: 0 });
}
function reuseFrom(doc) {
  return (coll, id, h) => { const e = doc.c && doc.c[coll] && doc.c[coll][id]; return e && e.h === h ? e.d : undefined; };
}

// Fields of the app state that sync reads (identity unchanged ⇒ nothing was edited).
const SYNCED_FIELDS = ['sessions', 'programs', 'exercises', 'bodyEntries', 'measurements', 'photos', 'prefs', 'activeProgramId', 'profile', 'welcomed', 'user', 'deleted', 'syncRestore'];
function applyDoc(doc, dev, joinPrefer = null, indexed = null, preMeta = null) {
  const h = host();
  // The updater may run later (React batches setState): re-index the state it
  // receives against the meta that describes the PRE-merge state, so an edit
  // made during the round trip is stamped and survives. On a join the same
  // precedence rules apply to that re-merge.
  const before = trackerMeta();
  const fn = (prev) => {
    let final = doc;
    // Common case: nothing was edited during the round trip — the doc already is the merge.
    if (!indexed || SYNCED_FIELDS.some((k) => prev[k] !== indexed[k])) {
      let { doc: cur } = indexState(prev, before, Date.now(), dev);
      if (joinPrefer) cur = applyJoinPolicy(cur, doc, joinPrefer).local;
      final = mergeDocs(cur, doc);
    }
    const next = docToState(prev, final);
    const m = final === doc && preMeta ? { ...preMeta, restoreId: prev && prev.syncRestore ? prev.syncRestore.id : undefined } : metaFromDoc(final, prev);
    m.hlc = Math.max(Number(before.hlc) || 0, Number(trackerMeta().hlc) || 0, maxDocT(final));
    setTrackerMeta(m);
    return next;
  };
  if (h && h.applyMergedState) h.applyMergedState(fn);
  else { const prev = lsGet(K.state, null); if (prev) lsSet(K.state, fn(prev)); }
}

// The synced copy was deleted from another device: stop syncing here, keep the data.
function revokedHere() {
  const c = cfg() || {};
  lsSet(K.cfg, { deviceId: c.deviceId, revokedAt: Date.now() });
  const acct = lsGet(K.account, null);
  if (acct && acct.token) lsSet(K.account, { notice: 'deleted', id: acct.id || '', at: Date.now() });
  keyCache = null; lastDoc = null;
  status = computeStatus();
  emit({});
}

// ───────────────────────────── enable / join / disable ─────────────────────────────

/** Create a new vault from this device's data. Returns the code. */
// Wait for a sync that is already running (it belongs to the previous code).
async function settle() { while (running) { try { await running; } catch (_) { /* reported by that run */ } } }

export async function enableSync(opts = {}) {
  await settle();
  if (!opts.skipProbe && !(await probeSyncServer())) throw syncError('no-server', 'The sync server isn’t available on this install.');
  // An account creates its vault key first (to wrap it), then turns sync on with it.
  const code = (opts.code && normalizeSyncCode(opts.code)) || newSyncCode();
  setCfg({ code, enabledAt: Date.now(), etag: null, lastHash: null, lastError: null, lastErrorCode: null, unreadable: null, joining: null, revokedAt: null, deviceId: currentDeviceId(), fresh: true });
  status = computeStatus();
  emit({});
  await syncNow({ force: true });
  return code;
}

/** Fetch + decrypt a vault without changing anything, for the merge preview. */
export async function previewJoin(input) {
  const code = normalizeSyncCode(input);
  if (!code) throw syncError('code', 'That code isn’t valid — check for typos');
  const keys = await deriveKeys(code);
  const r = await getRemote(keys.vaultId, null);
  if (r.status === 404) throw syncError('missing', 'No synced data found for this code');
  if (r.status === 410) throw syncError('revoked', 'This sync code was turned off (its synced copy was deleted)');
  const st = readLocalState() || {};
  await warmHashes(st);
  const { doc: local } = indexState(st, trackerMeta(), Date.now(), currentDeviceId());
  let remote;
  try { remote = await decryptDoc(r.bytes, keys, { reuse: reuseFrom(local) }); } catch (e) {
    if (e.code === 'newer') throw e;
    throw syncError('unreadable', 'The synced copy for this code can’t be read');
  }
  const localIds = new Set(Object.keys(local.c.sessions));
  const remoteIds = Object.keys(remote.c.sessions || {});
  const suggest = remoteIds.length === 0 && localIds.size > 0 ? 'local' : 'remote';
  const after = (prefer) => { const j = applyJoinPolicy(local, remote, prefer); return mergeDocs(j.local, j.remote); };
  const merged = after(suggest);
  return {
    code,
    remote: docCounts(remote),
    local: docCounts(local),
    shared: remoteIds.filter((id) => localIds.has(id)).length,
    remoteUpdatedAt: remote.at || null,
    afterMerge: docCounts(merged).sessions,
    afterPrograms: docCounts(merged).programs,
    suggest,
    settings: { local: docSummary(local), remote: docSummary(remote) },
  };
}

/**
 * Join an existing vault; local and remote data are merged. opts.prefer:
 * 'remote' (default — the synced copy's settings, name and active program win)
 * or 'local'. Sync is only switched on if the join succeeds.
 */
export async function joinSync(input, opts = {}) {
  const code = normalizeSyncCode(input);
  if (!code) throw syncError('code', 'That code isn’t valid — check for typos');
  // A sync still running for the old code must not answer for (or clear the
  // "joining" flag of) the new one.
  await settle();
  const before = cfg() || {};
  setCfg({ code, enabledAt: Date.now(), etag: null, lastHash: null, lastError: null, lastErrorCode: null, unreadable: null, revokedAt: null, deviceId: currentDeviceId(), fresh: false, joining: { prefer: opts.prefer === 'local' ? 'local' : 'remote' } });
  status = computeStatus();
  emit({});
  const st = await syncNow({ force: true });
  const after = cfg() || {};
  if (after.code === code && !after.joining && st.state !== 'error' && st.state !== 'offline') return st;
  // Failed (or turned off meanwhile): leave sync exactly as it was.
  const msg = st.state === 'offline' ? 'Can’t reach the sync server' : (st.error || 'Couldn’t join');
  const keep = { deviceId: after.deviceId || before.deviceId };
  if (before.code) Object.assign(keep, before); else if (after.revokedAt) keep.revokedAt = after.revokedAt;
  lsSet(K.cfg, keep);
  status = computeStatus();
  emit({});
  throw syncError(st.errorCode || 'join', msg);
}

/**
 * Fix an unreadable synced copy by overwriting it with this device's data
 * (only the damaged blob is replaced: the PUT is conditional on its ETag).
 */
export async function replaceSyncedCopy() {
  const c = cfg();
  if (!c || !c.code) return getSyncStatus();
  const state = readLocalState();
  const keys = await deriveKeys(c.code);
  const dev = c.deviceId || currentDeviceId();
  await warmHashes(state);
  const { doc } = indexNow(state, Date.now(), dev);
  emit({ state: 'syncing' });
  try {
    const bytes = await encryptDoc({ ...doc, at: Date.now(), by: dev }, keys);
    const put = await putRemote(keys.vaultId, bytes, c.unreadable ? c.unreadable.etag : (c.etag || null));
    if (put.revoked) { revokedHere(); return getSyncStatus(); }
    if (put.conflict) { setCfg({ unreadable: null, etag: null }); return syncNow({ force: true }); }
    setCfg({ etag: put.etag, lastHash: docHash(doc), lastSyncAt: Date.now(), lastError: null, lastErrorCode: null, unreadable: null, fresh: false });
    emit({ state: 'idle', pending: false, lastSyncAt: Date.now(), error: null, errorCode: null, canRepair: false });
  } catch (e) {
    emit({ state: e.code === 'offline' ? 'offline' : 'error', error: e.message, errorCode: e.code || 'error', canRepair: true });
    throw e;
  }
  return getSyncStatus();
}

/**
 * Stop syncing on this device. opts.erase also deletes the server copy: the
 * server keeps a "deleted" marker, and every other device turns sync off the
 * next time it connects. Throws (and leaves sync on) if the copy could not be
 * deleted. Returns { erased }.
 */
export async function disableSync(opts = {}) {
  const c = cfg();
  let erased = false;
  if (opts.erase && c && c.code) {
    const keys = await deriveKeys(c.code);
    const res = await api(`/sync/${keys.vaultId}`, { method: 'DELETE', headers: { 'If-Match': '*' } });
    if (res.status !== 200 && res.status !== 404 && res.status !== 410) throw httpError(res);
    erased = true;
  }
  const dev = c && c.deviceId;
  lsSet(K.cfg, dev ? { deviceId: dev } : {});
  if (!opts.keepAccount) { const a = lsGet(K.account, null); if (a && a.token) lsSet(K.account, opts.accountNotice ? { notice: opts.accountNotice, id: a.id || '', at: Date.now() } : {}); }
  keyCache = null; lastDoc = null;
  clearTimeout(debounceT); debounceT = null; pendingLive = false;
  status = computeStatus();
  emit({});
  return { erased };
}

// ───────────────────────────── accounts + photo hooks ─────────────────────────────
//
// account.js (sign-in) and photo_sync.js (encrypted photo blobs) build on this
// engine: the vault key is the sync code's 32 bytes, so an account wraps it and
// the photo store derives its own keys from it.

const syncedHooks = new Set();
/** fn(state) runs (async, errors ignored) after every successful sync. Returns unsubscribe. */
export function onSyncComplete(fn) { syncedHooks.add(fn); return () => syncedHooks.delete(fn); }
function afterSynced() {
  const st = readLocalState();
  for (const fn of syncedHooks) { try { Promise.resolve(fn(st)).catch(() => {}); } catch (_) { /* ignore */ } }
  checkAccountSession().catch(() => {});
}
/** The sync code's 32 key bytes. */
export function keyBytesFromCode(code) { const c = normalizeSyncCode(code); return c ? b32decode(c.slice(0, 52), 32) : null; }
/** 32 key bytes → the 54-char sync code. */
export function codeFromKeyBytes(bytes) { const b = new Uint8Array(bytes); return b.length === 32 ? b32encode(b) + checksum(b) : null; }
/** fetch() against the sync server (same base, environment and offline error as sync). */
export function syncApi(path, init) { return api(path, init); }
/** Crockford base32 + the 2-char checksum used by sync codes (recovery keys reuse it). */
export const base32 = { encode: b32encode, decode: b32decode, checksum };
/** This device's storage (localStorage, or the test environment's). */
export const localStore = { get: lsGet, set: lsSet, del: lsDel, keys: K };
export function currentLocalState() { return readLocalState(); }

// A device signed out from another device (Settings → Devices) stops syncing
// here on its next launch (first sync), or within 6 hours while it stays open.
const ACCOUNT_CHECK_MS = 6 * 3600000;
let accountCheckedThisLaunch = false;
async function checkAccountSession(force = false) {
  const a = lsGet(K.account, null);
  if (!a || !a.token) return null;
  if (!force && accountCheckedThisLaunch && a.checkedAt && Date.now() - a.checkedAt < ACCOUNT_CHECK_MS) return null;
  accountCheckedThisLaunch = true;
  let res;
  try { res = await api('/account/me', { headers: { Authorization: `Bearer ${a.token}` } }); } catch (_) { return null; }
  const me = res.ok ? await res.json().catch(() => null) : null;
  if (res.status === 401 || (me && me.signedOut)) {
    await disableSync({ accountNotice: 'signed-out' });
    return { signedOut: true };
  }
  if (!me) return null;
  const cur = lsGet(K.account, null);
  if (cur && cur.token === a.token) lsSet(K.account, { ...cur, checkedAt: Date.now(), devices: me && me.devices, recoveryAt: (me && me.recoveryAt) || cur.recoveryAt });
  return me;
}
export { checkAccountSession };

// ───────────────────────────── file backup ─────────────────────────────

function backupJSON(state, photoData) {
  const withPhotos = photoData && typeof photoData === 'object' && Object.keys(photoData).length > 0;
  return JSON.stringify({
    ...(withPhotos ? { photos: (state.photos || []).filter((p) => p && photoData[p.id]), photoData } : {}),
    schemaVersion: 1, app: 'liftlog', exportedAt: new Date().toISOString(),
    sessions: (state.sessions || []).filter((s) => s.completedAt),
    prs: state.prs || [], programs: state.programs || [], exercises: state.exercises || [],
    bodyEntries: state.bodyEntries || [], measurements: state.measurements || [],
    prefs: state.prefs || {}, user: { pro: !!(state.user && state.user.pro), name: (state.user && state.user.name) || '' },
    activeProgramId: state.activeProgramId || null, profile: state.profile || null, welcomed: !!state.welcomed,
    times: recordTimes(),
  });
}

/**
 * Share a full JSON backup (iCloud Drive / Google Drive / Files via the share
 * sheet); falls back to a download. Restorable with Settings → Import JSON.
 * opts.photoData = { <photoId>: 'data:image/webp;base64,…' } also embeds those
 * progress photos (and their records). Returns 'shared' | 'downloaded' | 'cancelled'.
 */
export async function backupToFile(state = readLocalState(), opts = {}) {
  const name = `liftlog-backup-${localDateStr(Date.now())}${opts.photoData ? '-with-photos' : ''}.json`;
  const blob = new Blob([backupJSON(state || {}, opts.photoData)], { type: 'application/json' });
  let file = null;
  try { file = new File([blob], name, { type: 'application/json' }); } catch (_) { /* old Safari */ }
  if (file && typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Lift Log backup' });
      markBackup();
      return 'shared';
    } catch (e) {
      if (e && e.name === 'AbortError') return 'cancelled';
      // fall through to download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  markBackup();
  return 'downloaded';
}
function markBackup() { lsSet(K.backup, { ...(lsGet(K.backup, {}) || {}), lastBackupAt: Date.now() }); }
export function lastBackupAt() { return (lsGet(K.backup, {}) || {}).lastBackupAt || null; }

/** Weekly nudge: sync is off, there is real history, and no backup/dismissal in 7 days. */
export function backupNudgeDue(state, now = Date.now()) {
  if (getSyncStatus().enabled) return false;
  const done = (state && state.sessions || []).filter((s) => s.completedAt).length;
  if (done < 3) return false;
  const b = lsGet(K.backup, {}) || {};
  const week = 7 * 86400000;
  return !(b.lastBackupAt && now - b.lastBackupAt < week) && !(b.dismissedAt && now - b.dismissedAt < week);
}
export function dismissBackupNudge() { lsSet(K.backup, { ...(lsGet(K.backup, {}) || {}), dismissedAt: Date.now() }); }

// ───────────────────────────── lock-screen rest alerts (Web Push) ─────────────────────────────
//
// The server sends the push at the absolute end time. To avoid a duplicate
// banner while the app is on screen (the in-app alert already fires), the job
// is only scheduled while the page is hidden (screen locked / app switched),
// and cancelled the moment it is visible again.

let armed = null;          // { at, title, body }
let scheduledAt = null;    // `at` currently queued on the server

export function isStandalone() {
  try { return !!(window.matchMedia('(display-mode: standalone)').matches || navigator.standalone); } catch (_) { return false; }
}
export function restPushState() {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
  const p = lsGet(K.push, null);
  const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent || '');
  return {
    supported,
    enabled: !!(p && p.on && p.sub),
    permission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
    standalone: isStandalone(),
    needsInstall: isIOS && !isStandalone(),
    isIOS,
  };
}
function b64urlToBytes(s) {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
}
async function swReady(ms = 6000) {
  if (!('serviceWorker' in navigator)) return null;
  return Promise.race([navigator.serviceWorker.ready, new Promise((r) => setTimeout(() => r(null), ms))]);
}

/** Must run from a user gesture. Returns { ok, reason }. */
export async function enableRestPush() {
  const st = restPushState();
  if (!st.supported) return { ok: false, reason: st.needsInstall ? 'install' : 'unsupported' };
  let perm = Notification.permission;
  if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch (_) { perm = 'default'; } }
  if (perm !== 'granted') return { ok: false, reason: perm === 'denied' ? 'denied' : 'dismissed' };
  let publicKey;
  try {
    const r = await api('/push/vapid');
    if (!r.ok) return { ok: false, reason: 'server' };
    publicKey = (await r.json()).publicKey;
  } catch (_) { return { ok: false, reason: 'server' }; }
  const reg = await swReady();
  if (!reg || !reg.pushManager) return { ok: false, reason: 'no-sw' };
  let sub;
  try {
    sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(publicKey) });
  } catch (e) { return { ok: false, reason: 'subscribe', detail: e && e.message }; }
  const json = sub.toJSON();
  try {
    const r = await api('/push/subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ subscription: json, test: true, title: 'Rest alerts are on', body: 'You’ll get a notification here when rest ends, even with your phone locked.' }) });
    if (!r.ok) return { ok: false, reason: 'server' };
  } catch (_) { return { ok: false, reason: 'server' }; }
  const prev = lsGet(K.push, null) || {};
  lsSet(K.push, { on: true, sub: json, tag: prev.tag || `rest-${hex(globalThis.crypto.getRandomValues(new Uint8Array(8)))}` });
  return { ok: true };
}
export async function disableRestPush() {
  await cancelRestAlert();
  lsSet(K.push, { ...(lsGet(K.push, null) || {}), on: false });
  if (getReminders().on) return; // training reminders still use this subscription
  try { const reg = await swReady(2000); const s = reg && await reg.pushManager.getSubscription(); if (s) await s.unsubscribe(); } catch (_) { /* ignore */ }
}

function postPush(path, body) {
  return api(path, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    .then((r) => r.ok).catch(() => false);
}
async function scheduleArmed() {
  const p = lsGet(K.push, null);
  if (!armed || !p || !p.on || !p.sub) return false;
  if (armed.at <= Date.now() + 500 || scheduledAt === armed.at) return scheduledAt === armed.at;
  scheduledAt = armed.at;
  const ok = await postPush('/push/schedule', { subscription: p.sub, at: armed.at, title: armed.title, body: armed.body, tag: p.tag });
  if (!ok) scheduledAt = null;
  return ok;
}
async function unscheduleIfAny() {
  const p = lsGet(K.push, null);
  if (scheduledAt == null || !p || !p.tag) return;
  scheduledAt = null;
  await postPush('/push/cancel', { tag: p.tag, subscription: p.sub }); // the server only lets the scheduling subscription cancel
}
let pushWired = false;
function wirePushVisibility() {
  if (pushWired || typeof document === 'undefined') return;
  pushWired = true;
  document.addEventListener('visibilitychange', () => { if (document.hidden) scheduleArmed(); else unscheduleIfAny(); });
  window.addEventListener('pagehide', () => { scheduleArmed(); });
}

/**
 * Arm a lock-screen alert for the end of the current rest. Call when rest starts
 * (and again if the user adds/subtracts time). No-ops (returns false) when push
 * is off/unsupported or the server is unreachable.
 */
export async function pushRestAlert({ at, title = 'Rest over', body = 'Time for your next set' } = {}) {
  const p = lsGet(K.push, null);
  if (!p || !p.on || !Number.isFinite(at)) return false;
  wirePushVisibility();
  if (armed && armed.at !== at) await unscheduleIfAny();
  armed = { at, title, body };
  if (typeof document !== 'undefined' && document.hidden) return scheduleArmed();
  return true;
}
/** Disarm (rest skipped / finished in-app / workout ended). */
export async function cancelRestAlert() {
  armed = null;
  await unscheduleIfAny();
}

// ───────────────────────────── training reminders (Web Push) ─────────────────────────────
//
// Settings → Reminders. The server keeps one schedule per device (keyed by a
// random secret only this device holds) in the user's IANA time zone and
// sends: a reminder on training days at the chosen time, a "missed workout"
// nudge the next morning, and a weekly backup nudge while sync is off. The
// schedule carries the next program day's name and the local dates with a
// completed workout, so a reminder is skipped once you've trained.
// observeReminders() re-sends it whenever those change (called by the bridge).

const REM_KEY = 'liftlog.reminders';
const WEEKDAY_NUM = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
export const REMINDER_DEFAULTS = {
  on: false,
  training: { on: true, time: '18:00', mode: 'program', weekdays: [1, 3, 5] },
  missed: { on: true, time: '08:00' },
  backup: { on: true, time: '19:00', weekday: 0 },
};
/** Saved reminder settings merged over the defaults (+ id, next, lastError, pendingSync). */
export function getReminders() {
  const r = lsGet(REM_KEY, null) || {};
  return {
    ...REMINDER_DEFAULTS, ...r,
    training: { ...REMINDER_DEFAULTS.training, ...(r.training || {}) },
    missed: { ...REMINDER_DEFAULTS.missed, ...(r.missed || {}) },
    backup: { ...REMINDER_DEFAULTS.backup, ...(r.backup || {}) },
  };
}
function patchReminders(patch) {
  const cur = getReminders();
  const next = {
    ...cur, ...patch,
    training: { ...cur.training, ...(patch.training || {}) },
    missed: { ...cur.missed, ...(patch.missed || {}) },
    backup: { ...cur.backup, ...(patch.backup || {}) },
  };
  lsSet(REM_KEY, next);
  for (const fn of remListeners) { try { fn(next); } catch (_) { /* ignore */ } }
  return next;
}
const remListeners = new Set();
/** Save reminder settings on this device only (the native app schedules them locally). */
export const patchReminderSettings = (patch) => patchReminders(patch);
export function subscribeReminders(fn) { remListeners.add(fn); return () => remListeners.delete(fn); }

/** What the device can do: { supported, needsInstall, permission, isIOS }. */
export function reminderSupport() {
  const st = restPushState();
  return { supported: st.supported, needsInstall: st.needsInstall && !st.supported, permission: st.permission, isIOS: st.isIOS };
}

const ymdLocal = (ms) => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
/** Weekdays (0 = Sunday) the active program's days are planned for. */
export function programWeekdays(state) {
  const p = (state && state.programs || []).find((x) => x.id === state.activeProgramId) || null;
  return [...new Set((p && p.days || []).map((d) => WEEKDAY_NUM[d.day]).filter((n) => n != null))].sort((a, b) => a - b);
}
/** The schedule sent to the server (pure apart from the clock / time zone). */
export function reminderSchedule(state, cfg = getReminders(), now = Date.now()) {
  const weekdays = cfg.training.mode === 'program' ? programWeekdays(state) : [...new Set(cfg.training.weekdays || [])].sort((a, b) => a - b);
  let label = '';
  try { const nx = nextWorkout(state, null, now); label = nx.day ? nx.day.name : ''; } catch (_) { /* no program */ }
  const done = (state && state.sessions || []).filter((s) => s && s.completedAt);
  const since = now - 21 * 86400000;
  let tz = 'UTC';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (_) { /* old engines */ }
  return {
    tz,
    training: { on: !!cfg.training.on, time: cfg.training.time, weekdays, label },
    missed: { on: !!cfg.missed.on, time: cfg.missed.time },
    backup: { on: !!cfg.backup.on, time: cfg.backup.time, weekday: cfg.backup.weekday },
    doneDates: [...new Set(done.filter((s) => (s.startedAt || s.completedAt) >= since).map((s) => ymdLocal(s.startedAt || s.completedAt)))].sort(),
    syncOn: !!getSyncStatus().enabled,
    lastBackupAt: lastBackupAt(),
    hasHistory: done.length >= 3,
  };
}

async function ensurePushSubscription() {
  const st = restPushState();
  if (!st.supported) return { ok: false, reason: st.needsInstall ? 'install' : 'unsupported' };
  let perm = Notification.permission;
  if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch (_) { perm = 'default'; } }
  if (perm !== 'granted') return { ok: false, reason: perm === 'denied' ? 'denied' : 'dismissed' };
  let publicKey;
  try {
    const r = await api('/push/vapid');
    if (!r.ok) return { ok: false, reason: r.status === 503 ? 'push-off' : 'server' };
    publicKey = (await r.json()).publicKey;
  } catch (_) { return { ok: false, reason: 'server' }; }
  const reg = await swReady();
  if (!reg || !reg.pushManager) return { ok: false, reason: 'no-sw' };
  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(publicKey) });
    return { ok: true, sub: sub.toJSON() };
  } catch (e) { return { ok: false, reason: 'subscribe', detail: e && e.message }; }
}

async function postReminders(cfg, state, { test = false, sub = cfg.sub } = {}) {
  const schedule = reminderSchedule(state, cfg);
  const id = cfg.id || hex(globalThis.crypto.getRandomValues(new Uint8Array(32)));
  let r;
  try {
    r = await api('/push/reminders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, subscription: sub, schedule, test }) });
  } catch (_) {
    patchReminders({ id, sub, pendingSync: true, lastError: 'server' });
    return { ok: false, reason: 'server' };
  }
  if (!r.ok) {
    const reason = r.status === 503 ? 'push-off' : r.status === 400 ? 'invalid' : 'server';
    patchReminders({ id, sub, pendingSync: true, lastError: reason });
    return { ok: false, reason };
  }
  const j = await r.json().catch(() => ({}));
  patchReminders({ id, sub, next: j.next || {}, lastHash: JSON.stringify(schedule), pendingSync: false, lastError: null, syncedAt: Date.now() });
  return { ok: true, next: j.next || {} };
}

/**
 * Save reminder settings (patch) and register them with the server. Turning
 * reminders on asks for notification permission, so call it from a tap.
 * → { ok, reason?, next? }  reasons: install | unsupported | denied | dismissed |
 *   server | push-off | no-sw | subscribe | invalid
 */
export async function saveReminders(state, patch = {}, { test = false } = {}) {
  const cfg = patchReminders(patch);
  if (!cfg.on) return disableReminders();
  const s = await ensurePushSubscription();
  if (!s.ok) { patchReminders({ lastError: s.reason }); return s; }
  return postReminders(cfg, state, { test, sub: s.sub });
}
/** Turn reminders off on this device (and drop the server's schedule). */
export async function disableReminders() {
  const cfg = patchReminders({ on: false, pendingSync: false, lastError: null, next: {} });
  if (cfg.id) {
    try { await api('/push/reminders/cancel', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: cfg.id }) }); } catch (_) { /* offline: it is dropped after 60 quiet days */ }
  }
  return { ok: true };
}

let remT = null;
/** Re-send the schedule when what it depends on changed (program days, workouts, sync, backups). */
export function observeReminders(state) {
  const cfg = getReminders();
  if (!cfg.on || !cfg.sub || !cfg.id || !state) return;
  const next = JSON.stringify(reminderSchedule(state, cfg));
  if (next === cfg.lastHash && !cfg.pendingSync) return;
  clearTimeout(remT);
  remT = setTimeout(() => { postReminders(getReminders(), state).catch(() => {}); }, 4000);
}

/**
 * Notification taps open the app at ?go=start | today | backup (or, when the
 * app is already open, the service worker posts { type: 'LIFTLOG_OPEN', go }).
 * start → resume the live workout or start the next program day;
 * today → that day's preview; backup → Sync & backup.
 */
export function openReminderLink(go, app) {
  const { state, setRoute } = app;
  if (go === 'backup') { setRoute('settings/sync'); return; }
  if (go !== 'start' && go !== 'today') return;
  const live = (state.sessions || []).find((s) => s.id === state.activeSessionId && !s.completedAt);
  if (live) { setRoute('active-workout', { dayId: live.dayId || undefined }); return; }
  let nx = null;
  try { nx = nextWorkout(state); } catch (_) { /* no program */ }
  if (!nx || !nx.day) { setRoute('home'); return; }
  if (go === 'start') setRoute('active-workout', { dayId: nx.day.id });
  else setRoute('workout-preview', { dayId: nx.day.id });
}
function useReminderLinks(ref) {
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const go = params.get('go');
    if (go) {
      params.delete('go');
      const q = params.toString();
      try { window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash); } catch (_) { /* ignore */ }
      setTimeout(() => openReminderLink(go, ref.current), 300);
    }
    const onMsg = (e) => { const d = e.data || {}; if (d.type === 'LIFTLOG_OPEN' && d.go) openReminderLink(d.go, ref.current); };
    const sw = navigator.serviceWorker;
    if (sw) sw.addEventListener('message', onMsg);
    return () => { if (sw) sw.removeEventListener('message', onMsg); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

// ───────────────────────────── React bridge ─────────────────────────────

/**
 * useSyncBridge(useApp()) — call once near the app root (e.g. in AppShell).
 * Registers the store with initSync, records edits as they happen
 * (observeLocalState → a sync is scheduled only when synced data changed), and
 * routes to 'settings/sync' when the app was opened via a #sync= link.
 * Uses actions.applySyncState(fn) (atomic setState(fn)) when the store has it,
 * else falls back to actions.applyStateJSON.
 */
export function useSyncBridge(app) {
  const ref = React.useRef(app);
  ref.current = app;
  React.useEffect(() => initSync({
    getState: () => ref.current.state,
    applyMergedState: (fn) => {
      const a = ref.current.actions;
      if (a.applySyncState) { a.applySyncState(fn); return; }
      // Fallback for stores without applySyncState (not atomic, but complete).
      const next = fn(ref.current.state);
      a.applyStateJSON(next);
      if (a.setActiveProgram && next.activeProgramId) a.setActiveProgram(next.activeProgramId);
      if (a.setUserName && next.user) a.setUserName(next.user.name || '');
      if (a.setProfile && next.profile) a.setProfile(next.profile);
    },
    onJoinLink: () => { if (ref.current.route && ref.current.route.name !== 'settings/sync') ref.current.setRoute('settings/sync'); },
  }), []);
  const { sessions, programs, exercises, bodyEntries, measurements, photos, prefs, activeProgramId, profile, welcomed, user, deleted, syncRestore, hydrated } = app.state;
  // Only finished workouts are synced: logging sets in a live workout keeps
  // this view (and so the edit index and the reminder schedule) untouched.
  const history = historyOf(sessions);
  React.useEffect(() => {
    // Until the store has loaded the full history from IndexedDB, indexing
    // would see records "vanish" — wait for it.
    if (hydrated === false) return;
    observeLocalState(ref.current.state);
    observeReminders(ref.current.state);
    if (deferredRun) { deferredRun = false; syncNow({ reason: 'start' }); }
  }, [history, programs, exercises, bodyEntries, measurements, photos, prefs, activeProgramId, profile, welcomed, user, deleted, syncRestore, hydrated]);
  useReminderLinks(ref);
}

// ───────────────────────────── test hooks ─────────────────────────────

export const __test = { b32encode, b32decode, checksum, stable, K, SEP, flushMeta, trackerMeta: () => trackerMeta(), workoutLive };
